import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast, Toaster } from 'sonner';
import { 
  Package, Clock, CheckCircle, Truck, ChefHat, MapPin, 
  DollarSign, BarChart, RefreshCw,
  Plus, Store, Banknote, CreditCard, Trash2, PlusCircle,
  Printer, Wallet, ArrowDownCircle, ArrowUpCircle, Receipt, Lock,
  CircleDollarSign, RotateCcw, XCircle, ChevronDown, ChevronRight,
  Edit, Power, ShoppingBag, Send, Copy, Crown, Search, Star, X, Gift,
  Smartphone, Beef, Drumstick, Sandwich, CupSoda, Salad, UtensilsCrossed, Sparkles,
  Flame, ArrowLeft, ChevronUp
} from 'lucide-react';
import { InvoiceToggle } from '../components/admin/InvoiceToggle';
import { ChargeInvoiceSection } from '../components/admin/ChargeInvoiceSection';
import { InvoicesTab } from '../components/admin/InvoicesTab';
import { computeInvoice } from '../lib/invoice';
import { InventoryTab } from '../components/admin/inventory/InventoryTab';
import { inventoryApi } from '../components/admin/inventory/inventoryApi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_INVOICE = { requires_invoice: false, rfc: '', razon_social: '', apply_isr: false };

// Visual icons + colors for each category (used in Manual Order screen)
const CATEGORY_VISUALS = {
  'Combos':              { icon: UtensilsCrossed, color: 'from-orange-500 to-red-600',     ring: 'ring-orange-400' },
  'Piezas de pollo':     { icon: Drumstick,       color: 'from-amber-500 to-orange-600',    ring: 'ring-amber-400' },
  'Pechugas':            { icon: Drumstick,       color: 'from-yellow-500 to-amber-600',    ring: 'ring-yellow-400' },
  'Papas':               { icon: Salad,           color: 'from-yellow-400 to-orange-500',   ring: 'ring-yellow-300' },
  'Hamburguesas de res': { icon: Beef,            color: 'from-red-600 to-rose-700',        ring: 'ring-red-400' },
  'Hamburguesas de pollo':{ icon: Sandwich,       color: 'from-orange-400 to-amber-600',    ring: 'ring-orange-300' },
  'Fajitas':             { icon: Flame,           color: 'from-rose-500 to-red-700',        ring: 'ring-rose-400' },
  'Refrescos':           { icon: CupSoda,         color: 'from-blue-500 to-cyan-600',       ring: 'ring-blue-400' },
  'Extras':              { icon: Sparkles,        color: 'from-purple-500 to-fuchsia-600',  ring: 'ring-purple-400' },
};

const STATUS_LABELS = {
  recibido: { label: 'Recibido', color: 'bg-yellow-500', icon: Package },
  preparando: { label: 'Preparando', color: 'bg-blue-500', icon: ChefHat },
  listo: { label: 'Listo', color: 'bg-green-500', icon: CheckCircle },
  en_camino: { label: 'En Camino', color: 'bg-indigo-500', icon: Truck },
  entregado: { label: 'Entregado', color: 'bg-gray-500', icon: MapPin }
};

const PAYMENT_STATUS_LABELS = {
  pendiente: { label: 'Pendiente', color: 'bg-yellow-600', textColor: 'text-yellow-400' },
  pagado: { label: 'Pagado', color: 'bg-green-600', textColor: 'text-green-400' },
  reembolsado: { label: 'Reembolsado', color: 'bg-red-600', textColor: 'text-red-400' }
};

const MENU_CATEGORIES = [
  "Combos",
  "Piezas de pollo",
  "Pechugas",
  "Papas",
  "Hamburguesas de res",
  "Hamburguesas de pollo",
  "Fajitas",
  "Refrescos",
  "Extras"
];

const COSTO_ENVIO = 50;

const PAYMENT_LABELS = {
  efectivo: { label: 'Efectivo', emoji: '💵' },
  transferencia: { label: 'Transferencia', emoji: '🏦' },
  tarjeta: { label: 'Tarjeta', emoji: '💳' }
};

const formatPayment = (method) => {
  const info = PAYMENT_LABELS[method] || PAYMENT_LABELS.efectivo;
  return `${info.emoji} ${info.label}`;
};

const paymentLabel = (method) => (PAYMENT_LABELS[method] || PAYMENT_LABELS.efectivo).label;

export const AdminPage = () => {
  const [orders, setOrders] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('orders');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Manual order form state
  const [manualOrder, setManualOrder] = useState({
    name: '',
    phone: '',
    address: '',
    notes: '',
    delivery_type: 'ventanilla',
    payment_method: 'efectivo',
    driver: 'independiente',
    items: [],
    vip_client: null,
    vip_points_to_redeem: 0,
    referral_code: '',
    referral_valid: null, // {valid, referrer_name, reason}
    invoice: EMPTY_INVOICE
  });
  const [discountPickerFor, setDiscountPickerFor] = useState(null); // index of item being edited
  const [isSubmittingManual, setIsSubmittingManual] = useState(false);

  // VIP state
  const [vipClients, setVipClients] = useState([]);
  const [vipSearchQuery, setVipSearchQuery] = useState('');
  const [vipSearchResults, setVipSearchResults] = useState([]);
  const [showVipSearch, setShowVipSearch] = useState(false);
  const [showVipForm, setShowVipForm] = useState(false);
  const [newVipClient, setNewVipClient] = useState({ name: '', phone: '', address: '', referral_code: '' });
  const [referralCodePreview, setReferralCodePreview] = useState(null); // {valid, name, reason}
  const [editingVipClient, setEditingVipClient] = useState(null);
  const [vipTabSearch, setVipTabSearch] = useState('');

  // Cash register state
  const [cashRegister, setCashRegister] = useState(null);
  const [cashMovements, setCashMovements] = useState([]);
  const [openingBalance, setOpeningBalance] = useState('');
  const [withdrawalAmount, setWithdrawalAmount] = useState('');
  const [withdrawalReason, setWithdrawalReason] = useState('');
  const [depositAmount, setDepositAmount] = useState('');
  const [depositReason, setDepositReason] = useState('');
  const [cashCutReport, setCashCutReport] = useState(null);
  const [cashHistory, setCashHistory] = useState([]);
  const [showCloseRegisterModal, setShowCloseRegisterModal] = useState(false);
  const [physicalCountInput, setPhysicalCountInput] = useState('');
  const [isClosingRegister, setIsClosingRegister] = useState(false);
  // Cobrar modal (cash change calculator)
  const [showChargeModal, setShowChargeModal] = useState(false);
  const [orderToCharge, setOrderToCharge] = useState(null);
  const [customerPaidWith, setCustomerPaidWith] = useState('');
  // Split payment state
  const [isSplitPayment, setIsSplitPayment] = useState(false);
  const [splitCash, setSplitCash] = useState('');
  const [splitCard, setSplitCard] = useState('');
  const [splitTransfer, setSplitTransfer] = useState('');
  // Driver cash-out prompt (for home delivery paid without enough cash)
  const [driverCashOutPrompt, setDriverCashOutPrompt] = useState(null); // {order, amount}
  // Manual order inline change calculator
  const [manualPaidWith, setManualPaidWith] = useState('');
  // Antoni (own driver) state
  const [antoniOrders, setAntoniOrders] = useState([]);
  const [antoniSummary, setAntoniSummary] = useState(null);
  const [showAntoniCutModal, setShowAntoniCutModal] = useState(false);
  const [antoniCutResult, setAntoniCutResult] = useState(null);
  // Receipts (historical) state
  const [receiptsDate, setReceiptsDate] = useState(() => {
    const now = new Date();
    const mx = new Date(now.getTime() - 6 * 60 * 60 * 1000);
    return mx.toISOString().split('T')[0];
  });
  const [receiptsData, setReceiptsData] = useState({ summary: null, receipts: [] });
  const [referralsStats, setReferralsStats] = useState(null);
  const [referralsList, setReferralsList] = useState([]);
  const [referralConfig, setReferralConfig] = useState({ percent: 2, active: true });
  const [referralFilters, setReferralFilters] = useState({ referrer_name: '', phone: '', code: '', status: '', date_from: '', date_to: '' });
  const [receiptsLoading, setReceiptsLoading] = useState(false);

  // VIP bulk invite modal
  const [showVipBulkInvite, setShowVipBulkInvite] = useState(false);

  // Catalog state
  const [catalog, setCatalog] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({});
  const [newItem, setNewItem] = useState({ name: '', description: '', price: '', category: 'Combos' });
  const [editingItem, setEditingItem] = useState(null);
  const [catalogSearch, setCatalogSearch] = useState('');
  const [catalogFilter, setCatalogFilter] = useState('Todos');
  const [manualCatalogSearch, setManualCatalogSearch] = useState('');
  const [manualCatalogFilter, setManualCatalogFilter] = useState('');
  const [reorderMode, setReorderMode] = useState(false);
  
  // Edit order state
  const [editingOrder, setEditingOrder] = useState(null);

  // Inventory state
  const [menuStock, setMenuStock] = useState({});
  const [lowStockCount, setLowStockCount] = useState(0);
  const prevLowStock = useRef(null);

  const fetchInventoryStatus = useCallback(async () => {
    try {
      const [status, alerts] = await Promise.all([inventoryApi.menuStatus(), inventoryApi.alerts()]);
      setMenuStock(status);
      setLowStockCount(alerts.count);
      const names = alerts.items.map(i => i.name);
      if (prevLowStock.current) {
        const fresh = names.filter(n => !prevLowStock.current.includes(n));
        if (fresh.length) toast.warning(`⚠️ Stock bajo: ${fresh.join(', ')}`, { duration: 5000 });
      }
      prevLowStock.current = names;
    } catch (err) {
      console.error(err);
    }
  }, []);

  // Filter today's orders - using Mexico timezone (UTC-6) to match cash register
  const todayOrders = useMemo(() => {
    const localTodayStr = () => {
      const now = new Date();
      // Convert to Mexico time (UTC-6) consistently
      const mxTime = new Date(now.getTime() - 6 * 60 * 60 * 1000);
      return mxTime.toISOString().split('T')[0]; // YYYY-MM-DD in Mexico TZ
    };
    const today = localTodayStr();
    return orders.filter(order => {
      const orderDate = new Date(order.created_at);
      const mxOrderDate = new Date(orderDate.getTime() - 6 * 60 * 60 * 1000);
      return mxOrderDate.toISOString().split('T')[0] === today;
    });
  }, [orders]);

  // Orders since cash register opened (precise alignment with cash cut)
  const ordersSinceCajaOpen = useMemo(() => {
    if (!cashRegister || !cashRegister.opened_at) return todayOrders;
    const openedAt = new Date(cashRegister.opened_at).getTime();
    return orders.filter(o => {
      const created = new Date(o.created_at).getTime();
      return created >= openedAt;
    });
  }, [orders, cashRegister, todayOrders]);

  // Calculate today's sales — all PAID orders from today's calendar day (UTC-6 Coahuila)
  // This way: a sale before opening caja still counts as today's sale
  const bizAmount = (o) => (o.total || 0) - (o.delivery_fee || 0);

  const paidOrdersForCaja = useMemo(() => {
    return todayOrders.filter(o => o.payment_status === 'pagado');
  }, [todayOrders]);
  
  // Allocate the biz-amount of an order to a method bucket, honoring payment_split when present
  const amountByMethod = (o, methodKey) => {
    const total = Number(o.total) || 0;
    const biz = total - (Number(o.delivery_fee) || 0);
    const sp = o.payment_split;
    const splitMap = { efectivo: 'cash', tarjeta: 'card', transferencia: 'transfer' };
    if (sp && total > 0) {
      const portion = Number(sp[splitMap[methodKey]] || 0);
      return (portion / total) * biz;
    }
    return o.payment_method === methodKey ? biz : 0;
  };
  
  const todayCashSales = useMemo(() => {
    return paidOrdersForCaja.reduce((sum, o) => sum + amountByMethod(o, 'efectivo'), 0);
  }, [paidOrdersForCaja]);

  const todayTransferSales = useMemo(() => {
    return paidOrdersForCaja.reduce((sum, o) => sum + amountByMethod(o, 'transferencia'), 0);
  }, [paidOrdersForCaja]);

  const todayCardSales = useMemo(() => {
    return paidOrdersForCaja.reduce((sum, o) => sum + amountByMethod(o, 'tarjeta'), 0);
  }, [paidOrdersForCaja]);

  const todayDeliveryFees = useMemo(() => {
    return paidOrdersForCaja.reduce((sum, o) => sum + (o.delivery_fee || 0), 0);
  }, [paidOrdersForCaja]);

  // Refunds today (calendar day)
  const todayRefundCash = useMemo(() => {
    return todayOrders
      .filter(o => o.payment_status === 'reembolsado')
      .reduce((sum, o) => sum + amountByMethod(o, 'efectivo'), 0);
  }, [todayOrders]);

  const totalWithdrawals = useMemo(() => {
    return cashMovements.filter(m => m.type === 'withdrawal').reduce((s, m) => s + (m.amount || 0), 0);
  }, [cashMovements]);

  const totalDeposits = useMemo(() => {
    return cashMovements.filter(m => m.type === 'deposit').reduce((s, m) => s + (m.amount || 0), 0);
  }, [cashMovements]);

  // Theoretical cash that should be in the drawer when closing
  const theoreticalCash = useMemo(() => {
    const opening = cashRegister?.opening_balance || 0;
    return opening + todayCashSales - todayRefundCash - totalWithdrawals + totalDeposits;
  }, [cashRegister, todayCashSales, todayRefundCash, totalWithdrawals, totalDeposits]);

  const fetchData = useCallback(async () => {
    try {
      const [ordersRes, reportRes, registerRes, movementsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/orders`),
        fetch(`${BACKEND_URL}/api/admin/reports/today`),
        fetch(`${BACKEND_URL}/api/cash-register/current`),
        fetch(`${BACKEND_URL}/api/cash-register/movements`)
      ]);

      if (ordersRes.ok) setOrders(await ordersRes.json());
      if (reportRes.ok) setReport(await reportRes.json());
      if (registerRes.ok) setCashRegister(await registerRes.json());
      if (movementsRes.ok) setCashMovements(await movementsRes.json());
      fetchInventoryStatus();
    } catch (err) {
      console.error(err);
      // Error fetching data - silent fail
    } finally {
      setLoading(false);
    }
  }, [fetchInventoryStatus]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Cash register functions
  const openCashRegister = async () => {
    if (!openingBalance || parseFloat(openingBalance) < 0) {
      toast.error('Ingresa un fondo inicial válido');
      return;
    }

    try {
      const res = await fetch(`${BACKEND_URL}/api/cash-register/open`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ opening_balance: parseFloat(openingBalance) })
      });

      if (res.ok) {
        toast.success('¡Caja abierta!');
        setOpeningBalance('');
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Error al abrir caja');
      }
    } catch (err) {
      console.error(err);
      toast.error('Error al abrir caja');
    }
  };

  const makeWithdrawal = async () => {
    if (!withdrawalAmount || parseFloat(withdrawalAmount) <= 0) {
      toast.error('Ingresa un monto válido');
      return;
    }
    if (!withdrawalReason.trim()) {
      toast.error('Ingresa el motivo del retiro');
      return;
    }

    try {
      const res = await fetch(`${BACKEND_URL}/api/cash-register/withdrawal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          amount: parseFloat(withdrawalAmount),
          reason: withdrawalReason 
        })
      });

      if (res.ok) {
        toast.success('Retiro registrado');
        setWithdrawalAmount('');
        setWithdrawalReason('');
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Error al registrar retiro');
      }
    } catch (err) {
      console.error(err);
      toast.error('Error al registrar retiro');
    }
  };

  const makeDeposit = async () => {
    if (!depositAmount || parseFloat(depositAmount) <= 0) {
      toast.error('Ingresa un monto válido');
      return;
    }
    if (!depositReason.trim()) {
      toast.error('Ingresa el motivo del ingreso');
      return;
    }

    try {
      const res = await fetch(`${BACKEND_URL}/api/cash-register/deposit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          amount: parseFloat(depositAmount),
          reason: depositReason 
        })
      });

      if (res.ok) {
        toast.success('Ingreso registrado');
        setDepositAmount('');
        setDepositReason('');
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Error al registrar ingreso');
      }
    } catch (err) {
      console.error(err);
      toast.error('Error al registrar ingreso');
    }
  };

  const closeCashRegister = async () => {
    // Fetch pending orders check BEFORE opening the close modal so we can warn the user
    try {
      const res = await fetch(`${BACKEND_URL}/api/cash-register/pending-check`);
      if (res.ok) {
        const data = await res.json();
        if (data.pending_count && data.pending_count > 0) {
          const proceed = window.confirm(
            `⚠️ ATENCIÓN\n\n` +
            `Tienes ${data.pending_count} pedido(s) en estado PENDIENTE por un total de $${data.pending_total.toFixed(2)}.\n\n` +
            `Estos pedidos NO se contarán en el corte de caja. Si ya cobraste el efectivo, ciérralos primero (Cobrar) antes de hacer el corte.\n\n` +
            `¿Continuar con el corte de todas formas?`
          );
          if (!proceed) return;
        }
      }
    } catch (err) {
      console.warn('Pending check failed', err);
    }
    setPhysicalCountInput('');
    setShowCloseRegisterModal(true);
  };

  const confirmCloseCashRegister = async () => {
    const parsed = parseFloat(physicalCountInput);
    if (Number.isNaN(parsed) || parsed < 0) {
      toast.error('Ingresa el efectivo físico contado (un número válido)');
      return;
    }

    try {
      setIsClosingRegister(true);
      const res = await fetch(`${BACKEND_URL}/api/cash-register/close`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ counted_cash: parsed })
      });

      if (res.ok) {
        const report = await res.json();
        setCashCutReport(report);
        setShowCloseRegisterModal(false);
        setPhysicalCountInput('');
        toast.success('¡Corte de caja generado!');
        fetchData();
        fetchCashHistory();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Error al cerrar caja');
      }
    } catch (err) {
      console.error(err);
      toast.error('Error al cerrar caja');
    } finally {
      setIsClosingRegister(false);
    }
  };

  const fetchCashHistory = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/cash-register/history`);
      if (res.ok) {
        setCashHistory(await res.json());
      }
    } catch (err) {
      console.error(err);
      // Silent fail
    }
  };

  const fetchAntoniData = async () => {
    try {
      const [ordersRes, summaryRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/antoni/orders`),
        fetch(`${BACKEND_URL}/api/antoni/summary`)
      ]);
      if (ordersRes.ok) setAntoniOrders(await ordersRes.json());
      if (summaryRes.ok) setAntoniSummary(await summaryRes.json());
    } catch (err) {
      console.error(err);
    }
  };

  // Historical receipts
  // ---- Referral program helpers ----
  const fetchReferralsStats = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/referrals/stats`);
      if (res.ok) setReferralsStats(await res.json());
    } catch (e) { console.error(e); }
  };
  const fetchReferralsList = async (filters = null) => {
    try {
      const q = new URLSearchParams();
      const f = filters || referralFilters;
      Object.entries(f).forEach(([k, v]) => { if (v) q.append(k, v); });
      const res = await fetch(`${BACKEND_URL}/api/admin/referrals/list?${q.toString()}`);
      if (res.ok) setReferralsList(await res.json());
    } catch (e) { console.error(e); }
  };
  const fetchReferralConfig = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/referrals/config`);
      if (res.ok) setReferralConfig(await res.json());
    } catch (e) { console.error(e); }
  };
  const updateReferralConfig = async () => {
    const pin = window.prompt('PIN de administrador (4 dígitos):');
    if (!pin) return;
    const newPct = window.prompt(`Nuevo porcentaje (actual: ${referralConfig.percent}%):`, String(referralConfig.percent));
    if (newPct === null) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/referrals/config`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ current_pin: pin, percent: parseFloat(newPct) })
      });
      if (res.ok) { toast.success('✅ Porcentaje actualizado'); fetchReferralConfig(); }
      else { const err = await res.json(); toast.error(`❌ ${err.detail || 'Error'}`); }
    } catch (e) { toast.error('Error de red'); }
  };
  const toggleReferralActive = async () => {
    const pin = window.prompt('PIN de administrador:');
    if (!pin) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/referrals/config`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ current_pin: pin, active: !referralConfig.active })
      });
      if (res.ok) { toast.success('✅ Estado actualizado'); fetchReferralConfig(); }
      else { const err = await res.json(); toast.error(`❌ ${err.detail || 'Error'}`); }
    } catch (e) { toast.error('Error de red'); }
  };
  const recomputeReferralCounts = async () => {
    if (!window.confirm('¿Recalcular los contadores de recomendaciones de todos los VIPs a partir del histórico de pedidos?')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/referrals/recompute-counts`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        toast.success(`✅ ${data.recomputed} recomendadores actualizados`);
        fetchReferralsStats();
        fetchVipClients();
      } else {
        toast.error('Error al recalcular');
      }
    } catch (e) { toast.error('Error de red'); }
  };
  const settlePendingReferralRewards = async () => {
    if (!window.confirm('¿Acreditar puntos de recomendación pendientes en órdenes ya pagadas?')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/referrals/settle-pending`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        toast.success(`✅ ${data.settled} órdenes acreditadas (${data.skipped} omitidas)`);
        fetchReferralsStats();
        fetchReferralsList();
      } else {
        toast.error('Error al acreditar pendientes');
      }
    } catch (e) { toast.error('Error de red'); }
  };
  const cancelReferralMovement = async (id) => {
    const pin = window.prompt('PIN de administrador para cancelar:');
    if (!pin) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/referrals/${id}/cancel?pin=${encodeURIComponent(pin)}`, { method: 'POST' });
      if (res.ok) { toast.success('✅ Movimiento cancelado'); fetchReferralsStats(); fetchReferralsList(); }
      else { const err = await res.json(); toast.error(`❌ ${err.detail || 'Error'}`); }
    } catch (e) { toast.error('Error de red'); }
  };

  const fetchReceipts = useCallback(async (date) => {    const targetDate = date || receiptsDate;
    setReceiptsLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/receipts?date=${targetDate}`);
      if (res.ok) {
        setReceiptsData(await res.json());
      } else {
        toast.error('Error al cargar recibos');
      }
    } catch (err) {
      console.error(err);
      toast.error('Error al cargar recibos');
    } finally {
      setReceiptsLoading(false);
    }
  }, [receiptsDate]);

  const settleAntoniOrder = async (orderId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/orders/${orderId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ antoni_settled: true })
      });
      if (res.ok) {
        toast.success('✅ Pedido marcado como pagado por Antoni');
        fetchAntoniData();
      } else {
        toast.error('Error al actualizar pedido');
      }
    } catch (err) {
      console.error(err);
      toast.error('Error al actualizar pedido');
    }
  };

  const settleAllAntoniPending = async () => {
    if (!antoniSummary || antoniSummary.pending_count === 0) {
      toast.error('No hay pedidos pendientes de Antoni');
      return;
    }
    try {
      const res = await fetch(`${BACKEND_URL}/api/antoni/settle-pending`, {
        method: 'POST'
      });
      if (res.ok) {
        const data = await res.json();
        setAntoniCutResult({
          settled_count: data.settled_count,
          total: antoniSummary.pending_total,
          delivery_fees: antoniSummary.pending_delivery_fees,
          date: new Date().toLocaleString('es-MX')
        });
        setShowAntoniCutModal(false);
        toast.success(`✅ Corte realizado · ${data.settled_count} pedidos liquidados`);
        fetchAntoniData();
      } else {
        toast.error('Error al hacer corte');
      }
    } catch (err) {
      console.error(err);
      toast.error('Error al hacer corte');
    }
  };

  // Catalog functions
  const fetchCatalog = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/catalog`);
      if (res.ok) {
        setCatalog(await res.json());
      }
    } catch (err) {
      console.error(err);
      // Silent fail
    }
  };

  // Reorder a product up/down within its category
  const moveCatalogItem = async (category, itemId, direction) => {
    const items = (catalog[category] || []).slice();
    const idx = items.findIndex(it => it.id === itemId);
    if (idx === -1) return;
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= items.length) return;
    // Swap
    [items[idx], items[newIdx]] = [items[newIdx], items[idx]];
    // Optimistic UI update
    setCatalog(prev => ({ ...prev, [category]: items }));
    try {
      const res = await fetch(`${BACKEND_URL}/api/catalog/reorder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category, item_ids: items.map(i => i.id) })
      });
      if (!res.ok) {
        toast.error('Error al reordenar');
        fetchCatalog();
      }
    } catch (err) {
      console.error(err);
      toast.error('Error al reordenar');
      fetchCatalog();
    }
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const createMenuItem = async () => {
    if (!newItem.name || !newItem.price) {
      toast.error('Ingresa nombre y precio');
      return;
    }

    try {
      const res = await fetch(`${BACKEND_URL}/api/catalog/items`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newItem,
          price: parseFloat(newItem.price)
        })
      });

      if (res.ok) {
        toast.success('Artículo creado');
        setNewItem({ name: '', description: '', price: '', category: 'Combos' });
        fetchCatalog();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Error al crear');
      }
    } catch (err) {
      console.error(err);
      toast.error('Error al crear artículo');
    }
  };

  const updateMenuItem = async () => {
    if (!editingItem) return;

    try {
      const res = await fetch(`${BACKEND_URL}/api/catalog/items/${editingItem.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: editingItem.name,
          description: editingItem.description,
          price: parseFloat(editingItem.price),
          category: editingItem.category
        })
      });

      if (res.ok) {
        toast.success('Artículo actualizado');
        setEditingItem(null);
        fetchCatalog();
      }
    } catch (err) {
      console.error(err);
      toast.error('Error al actualizar');
    }
  };

  const deleteMenuItem = async (itemId) => {
    if (!window.confirm('¿Eliminar este artículo?')) return;

    try {
      const res = await fetch(`${BACKEND_URL}/api/catalog/items/${itemId}`, {
        method: 'DELETE'
      });

      if (res.ok) {
        toast.success('Artículo eliminado');
        fetchCatalog();
      }
    } catch (err) {
      console.error(err);
      toast.error('Error al eliminar');
    }
  };

  const toggleItemAvailability = async (itemId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/catalog/items/${itemId}/toggle`, {
        method: 'PATCH'
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(data.available ? 'Artículo disponible' : 'Artículo no disponible');
        fetchCatalog();
      }
    } catch (err) {
      console.error(err);
      toast.error('Error al cambiar disponibilidad');
    }
  };

  // ===== VIP Client functions =====
  const fetchVipClients = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/vip/clients`);
      if (res.ok) {
        setVipClients(await res.json());
      }
    } catch (err) {
      console.error(err);
      // Silent fail
    }
  }, []);

  const searchVipClients = async (query) => {
    if (!query || query.length < 2) {
      setVipSearchResults([]);
      return;
    }
    try {
      const res = await fetch(`${BACKEND_URL}/api/vip/clients/search?q=${encodeURIComponent(query)}`);
      if (res.ok) {
        setVipSearchResults(await res.json());
      }
    } catch (err) {
      console.error(err);
      // Silent fail
    }
  };

  const createVipClient = async () => {
    if (!newVipClient.name || !newVipClient.phone) {
      toast.error('Nombre y teléfono son obligatorios');
      return;
    }
    try {
      const res = await fetch(`${BACKEND_URL}/api/vip/clients`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newVipClient.name, phone: newVipClient.phone, address: newVipClient.address })
      });
      if (res.ok) {
        const created = await res.json();
        // If a referral code was entered and validated, link it
        const codeToLink = (newVipClient.referral_code || '').trim().toUpperCase();
        if (codeToLink && referralCodePreview?.valid) {
          try {
            const linkRes = await fetch(`${BACKEND_URL}/api/vip/clients/${created.id}/link-referrer`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ referral_code: codeToLink })
            });
            if (linkRes.ok) {
              const linkData = await linkRes.json();
              toast.success(`✅ Vinculado con recomendador ${linkData.referrer_name}`);
            } else {
              const err = await linkRes.json();
              toast.error(`⚠️ Cliente creado pero código no se pudo vincular: ${err.detail}`);
            }
          } catch (e) { /* non-fatal */ }
        }
        toast.success(`Cliente VIP creado. Código: ${created.code} · Recomendación: ${created.referral_code}`);
        setNewVipClient({ name: '', phone: '', address: '', referral_code: '' });
        setReferralCodePreview(null);
        setShowVipForm(false);
        fetchVipClients();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Error al crear cliente VIP');
      }
    } catch (err) {
      console.error(err);
      toast.error('Error al crear cliente VIP');
    }
  };

  const validateManualReferralCode = async () => {
    const code = (manualOrder.referral_code || '').trim().toUpperCase();
    if (!code) {
      setManualOrder(prev => ({ ...prev, referral_valid: null }));
      return;
    }
    try {
      const res = await fetch(`${BACKEND_URL}/api/referrals/validate/${encodeURIComponent(code)}`);
      const data = await res.json();
      setManualOrder(prev => ({ ...prev, referral_valid: data }));
      if (data.valid) {
        toast.success(`✅ Recomendado por ${data.referrer_name}`);
      } else {
        const msg = data.reason === 'not_found' ? 'Código no encontrado' :
                    data.reason === 'blocked' ? 'Código bloqueado' :
                    'Código inválido';
        toast.error(`❌ ${msg}`);
      }
    } catch (e) {
      toast.error('Error validando código');
    }
  };

  const validateReferralCodeInput = async () => {
    const code = (newVipClient.referral_code || '').trim().toUpperCase();
    if (!code) { setReferralCodePreview({ valid: false, reason: 'empty' }); return; }
    try {
      const res = await fetch(`${BACKEND_URL}/api/referrals/validate/${encodeURIComponent(code)}`);
      const data = await res.json();
      setReferralCodePreview(data);
      if (data.valid) {
        toast.success(`✅ Código válido: recomendado por ${data.referrer_name}`);
      } else {
        const msg = data.reason === 'not_found' ? 'No encontramos este código' :
                    data.reason === 'blocked' ? 'Este código está bloqueado' :
                    data.reason === 'self' ? 'No puedes usar tu propio código' :
                    'Código inválido';
        toast.error(`❌ ${msg}`);
      }
    } catch (e) { toast.error('Error validando'); }
  };

  const updateVipClient = async () => {
    if (!editingVipClient) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/vip/clients/${editingVipClient.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: editingVipClient.name,
          phone: editingVipClient.phone,
          address: editingVipClient.address,
          rfc: (editingVipClient.rfc || '').trim().toUpperCase(),
          razon_social: (editingVipClient.razon_social || '').trim()
        })
      });
      if (res.ok) {
        toast.success('Cliente VIP actualizado');
        setEditingVipClient(null);
        fetchVipClients();
      }
    } catch (err) {
      console.error(err);
      toast.error('Error al actualizar');
    }
  };

  const deleteVipClient = async (clientId) => {
    if (!window.confirm('¿Eliminar este cliente VIP?')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/vip/clients/${clientId}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        toast.success('Cliente VIP eliminado');
        fetchVipClients();
      }
    } catch (err) {
      console.error(err);
      toast.error('Error al eliminar');
    }
  };

  const selectVipForOrder = (client) => {
    setManualOrder(prev => ({
      ...prev,
      vip_client: client,
      // Auto-fill customer data if empty
      name: prev.name || client.name,
      phone: prev.phone || client.phone,
      address: prev.address || client.address
    }));
    setShowVipSearch(false);
    setVipSearchQuery('');
    setVipSearchResults([]);
    toast.success(`Cliente VIP: ${client.name} (${client.points?.toFixed(2) || 0} pts)`);
  };

  const removeVipFromOrder = () => {
    setManualOrder(prev => ({
      ...prev,
      vip_client: null,
      vip_points_to_redeem: 0
    }));
  };

  // Print ticket function with cash drawer kick command
  // Open the cash drawer by printing a minimal ticket (relies on the printer driver's
  // "open cash drawer before print" setting via ESC/POS pulse pin 2).
  const openCashDrawer = () => {
    const now = new Date();
    const timeStr = now.toLocaleTimeString('es-MX');
    const dateStr = now.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Apertura de Caja</title>
        <style>
          @page { margin: 0; size: 58mm auto; }
          * { margin: 0; padding: 0; box-sizing: border-box; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          body { font-family: Arial, sans-serif; width: 56mm; margin: 0 auto; padding: 4mm 2mm; color: #000; font-size: 11px; text-align: center; }
          .title { font-size: 13px; font-weight: bold; margin-bottom: 2mm; }
          .sub { font-size: 10px; color: #555; }
          .divider { border-top: 1px dashed #000; margin: 2mm 0; }
          .cmd { display: none; }
        </style>
      </head>
      <body>
        <div class="cmd" aria-hidden="true">&#x1B;p&#x00;&#x19;&#xFA;</div>
        <div class="title">APERTURA DE CAJA</div>
        <div class="divider"></div>
        <div class="sub">${dateStr}</div>
        <div class="sub">${timeStr}</div>
        <div class="divider"></div>
        <div class="sub">Mr. Crunch 28</div>
      </body>
      </html>
    `;

    const w = window.open('', '_blank', 'width=400,height=300');
    if (!w) {
      toast.error('No se pudo abrir la ventana de impresión (¿popups bloqueados?)');
      return;
    }
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => {
      try {
        w.print();
        setTimeout(() => w.close(), 500);
      } catch (err) {
        console.error(err);
        toast.error('Error al imprimir');
      }
    }, 250);
    toast.success('🖨️ Abriendo cajón...');
  };

  const printTicket = (order, openDrawer = true) => {
    // Clean internal tags from notes before printing
    const cleanNotes = (order.notes || '')
      .replace(/\[PEDIDO MANUAL\]/gi, '')
      .replace(/\[CORTESÍA\]/gi, '')
      .replace(/\[CORTESIA\]/gi, '')
      .replace(/\s+/g, ' ')
      .trim();

    const ticketContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Ticket</title>
        <style>
          @page {
            margin: 0;
            size: 58mm auto;
          }
          * { 
            margin: 0; 
            padding: 0; 
            box-sizing: border-box;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          body { 
            font-family: 'Arial', 'Helvetica', sans-serif;
            width: 56mm;
            margin: 0 auto;
            padding: 3mm 1.5mm;
            font-size: 13px;
            line-height: 1.45;
            color: #000;
            font-weight: 500;
          }
          .center { text-align: center; }
          .right { text-align: right; }
          
          .header {
            text-align: center;
            margin-bottom: 3mm;
          }
          .header .logo {
            display: block;
            margin: 0 auto 1.5mm;
            max-width: 32mm;
            height: auto;
          }
          .header h1 {
            font-size: 20px;
            font-weight: 500;
            letter-spacing: 1px;
            margin: 0;
          }
          .header h2 {
            font-size: 12px;
            font-weight: 500;
            margin: 0.5mm 0 1.5mm;
            letter-spacing: 0.5px;
          }
          .header .address {
            font-size: 10.5px;
            margin: 1mm 0;
            line-height: 1.3;
          }
          .header .whatsapp {
            font-size: 11px;
            margin: 1.5mm 0;
            line-height: 1.4;
          }
          .header .whatsapp .num {
            font-weight: 500;
            font-size: 12px;
          }
          
          .divider {
            text-align: center;
            margin: 2mm 0;
            font-size: 10px;
            letter-spacing: 0.5px;
            color: #000;
          }
          
          .info-section {
            margin: 1.5mm 0;
          }
          .info-row {
            display: flex;
            justify-content: space-between;
            font-size: 12px;
            margin: 1mm 0;
          }
          .info-row .label {
            color: #333;
          }
          
          .order-type {
            text-align: center;
            font-weight: 500;
            font-size: 13px;
            margin: 2mm 0;
            padding: 1mm 0;
            letter-spacing: 1px;
          }
          
          .items-section {
            margin: 2mm 0;
          }
          .item-row {
            display: flex;
            justify-content: space-between;
            font-size: 12.5px;
            margin: 0.8mm 0;
          }
          .item-row.item-name {
            font-weight: 500;
            margin-top: 1.5mm;
          }
          .item-row.item-detail {
            font-size: 11.5px;
            color: #222;
          }
          .item-row.item-discount {
            font-size: 11px;
            color: #333;
            padding-left: 3mm;
          }
          
          .totals-section {
            margin: 2mm 0 1mm;
          }
          .total-row {
            display: flex;
            justify-content: space-between;
            font-size: 12.5px;
            margin: 1mm 0;
          }
          .total-row.grand-total {
            font-size: 18px;
            font-weight: 800;
            margin: 2mm 0 0;
            padding: 1.5mm 0;
            border-top: 1.2px solid #000;
            border-bottom: 1.2px solid #000;
            letter-spacing: 0.5px;
          }
          
          .payment {
            text-align: center;
            font-size: 12px;
            margin: 2mm 0 1mm;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          
          .courtesy-stamp {
            text-align: center;
            border: 1.5px solid #000;
            padding: 2mm;
            margin: 2.5mm 0;
            font-size: 14px;
            font-weight: 500;
            letter-spacing: 2px;
          }
          
          .vip-stamp {
            text-align: center;
            border: 1px dashed #000;
            padding: 2mm;
            margin: 2mm 0;
          }
          .vip-stamp .vip-title {
            font-size: 12px;
            font-weight: 500;
            letter-spacing: 1px;
          }
          .vip-stamp .vip-name {
            font-size: 12px;
            margin-top: 1mm;
            font-weight: 500;
          }
          .vip-stamp .vip-code {
            font-size: 11px;
          }
          .vip-stamp .vip-pts {
            font-size: 11.5px;
            margin-top: 1mm;
          }
          
          .notes-section {
            margin: 1.5mm 0;
            font-size: 11.5px;
            line-height: 1.4;
          }
          .notes-section .label {
            font-weight: 500;
          }
          
          .footer {
            text-align: center;
            margin-top: 3mm;
          }
          .footer .thanks {
            font-size: 12.5px;
            font-weight: 500;
            letter-spacing: 0.5px;
          }
          
          @media print {
            html, body {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            body { 
              width: 56mm;
              padding: 1mm;
            }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <img class="logo" src="${BACKEND_URL.replace('/api','').replace(/\/$/,'')}/logo_ticket.png" alt="" onerror="this.style.display='none'" />
          <h1>MR. CRUNCH 28</h1>
          <h2>FRIED CHICKEN</h2>
          <div class="address">
            Luis G. Urbina #1490<br>
            Col. 28 de Junio<br>
            Acuña, Coahuila, 26250
          </div>
          <div class="whatsapp">
            Servicio a domicilio:<br>
            WhatsApp <span class="num">877 122 8589</span>
          </div>
        </div>
        
        <div class="divider">--------------------------------</div>
        
        <div class="info-section">
          <div class="info-row">
            <span class="label">Recibo #</span>
            <span>${order.id.slice(0,8)}</span>
          </div>
          <div class="info-row">
            <span class="label">Fecha</span>
            <span>${new Date(order.created_at).toLocaleDateString('es-MX')} · ${new Date(order.created_at).toLocaleTimeString('es-MX', {hour: '2-digit', minute:'2-digit'})}</span>
          </div>
        </div>
        
        <div class="divider">--------------------------------</div>
        
        <div class="order-type">
          ${order.delivery_type === 'domicilio' ? 'A DOMICILIO' : 'PARA LLEVAR'}
        </div>
        
        ${(order.customer_name && order.customer_name !== 'Ventanilla') || order.delivery_type === 'domicilio' ? `
        <div class="info-section">
          ${order.customer_name && order.customer_name !== 'Ventanilla' ? `
          <div class="info-row">
            <span class="label">Cliente</span>
            <span>${order.customer_name}</span>
          </div>
          ` : ''}
          ${order.customer_phone && order.customer_phone !== '-' ? `
          <div class="info-row">
            <span class="label">Tel</span>
            <span>${order.customer_phone}</span>
          </div>
          ` : ''}
          ${order.delivery_type === 'domicilio' ? `
          <div class="info-row" style="flex-direction:column;align-items:flex-start;">
            <span class="label">Dirección</span>
            <span style="font-size:11.5px;margin-top:0.5mm;">${order.customer_address}</span>
          </div>
          ` : ''}
        </div>
        <div class="divider">--------------------------------</div>
        ` : ''}
        
        <div class="items-section">
          ${order.items?.map(item => {
            const disc = Number(item.discount_percent) || 0;
            const grossLine = item.price * item.quantity;
            const finalLine = grossLine * (1 - disc/100);
            return `
            <div class="item-row item-name">
              <span>${item.name}${disc > 0 ? (disc === 100 ? ' (CORTESIA)' : ` (-${disc}%)`) : ''}</span>
            </div>
            <div class="item-row item-detail">
              <span>${item.quantity} x $${item.price.toFixed(2)}</span>
              <span>$${finalLine.toFixed(2)}</span>
            </div>
            ${disc > 0 && disc < 100 ? `
            <div class="item-row item-discount">
              <span>Desc ${disc}%</span>
              <span>-$${(grossLine - finalLine).toFixed(2)}</span>
            </div>
            ` : ''}
            ${item.notes && item.notes.trim() ? `
            <div class="item-row" style="font-style: italic; padding-left: 8px;">
              <span>-> ${item.notes}</span>
            </div>
            ` : ''}
          `;}).join('')}
        </div>
        
        <div class="divider">--------------------------------</div>
        
        <div class="totals-section">
          ${(order.referrer_vip_id && (order.subtotal || 0) >= 150) ? `
          <div style="border:2px dashed #000; padding:6px; margin:6px 0; text-align:center; font-weight:900; font-size:12px;">
            🌯 INCLUIR BURRITO CORTESÍA<br/>
            <span style="font-size:9px; font-weight:normal;">Promoción por código de recomendación</span>
          </div>
          ` : ''}
          ${order.delivery_type === 'domicilio' ? `
          <div class="total-row">
            <span>Subtotal</span>
            <span>$${(order.subtotal || (order.total - (order.delivery_fee || 0))).toFixed(2)}</span>
          </div>
          <div class="total-row">
            <span>Envío</span>
            <span>$${(order.delivery_fee || 50).toFixed(2)}</span>
          </div>
          ` : ''}
          ${order.discount_amount > 0 ? `
          <div class="total-row">
            <span>${order.is_courtesy ? 'Cortesía' : 'Descuentos'}</span>
            <span>-$${order.discount_amount.toFixed(2)}</span>
          </div>
          ` : ''}
          ${order.vip_discount_applied > 0 ? `
          <div class="total-row">
            <span>Descuento VIP</span>
            <span>-$${order.vip_discount_applied.toFixed(2)}</span>
          </div>
          ` : ''}
          ${order.requires_invoice ? `
          <div style="border-top:1px dashed #000; margin-top:1mm; padding-top:1mm;">
            <div class="total-row" style="font-weight:700;"><span>FACTURA</span><span>RFC: ${order.invoice_rfc || '-'}</span></div>
            ${order.invoice_razon_social ? `<div class="total-row"><span>${order.invoice_razon_social}</span></div>` : ''}
            <div class="total-row"><span>Subtotal</span><span>$${(order.invoice_base || 0).toFixed(2)}</span></div>
            <div class="total-row"><span>IVA 8%</span><span>$${(order.invoice_iva || 0).toFixed(2)}</span></div>
            ${(order.invoice_isr || 0) > 0 ? `<div class="total-row"><span>Ret. ISR 1.25%</span><span>-$${order.invoice_isr.toFixed(2)}</span></div>` : ''}
          </div>
          ` : ''}
          <div class="total-row grand-total">
            <span>TOTAL</span>
            <span>$${order.total.toFixed(2)}</span>
          </div>
        </div>
        
        <div class="payment">
          ${order.payment_split ? 'Pago dividido' : paymentLabel(order.payment_method)}
        </div>

        ${order.payment_split ? `
        <div class="totals-section" style="margin-top: 1mm;">
          ${(order.payment_split.cash || 0) > 0 ? `<div class="total-row"><span>Efectivo</span><span>$${order.payment_split.cash.toFixed(2)}</span></div>` : ''}
          ${(order.payment_split.card || 0) > 0 ? `<div class="total-row"><span>Tarjeta</span><span>$${order.payment_split.card.toFixed(2)}</span></div>` : ''}
          ${(order.payment_split.transfer || 0) > 0 ? `<div class="total-row"><span>Transferencia</span><span>$${order.payment_split.transfer.toFixed(2)}</span></div>` : ''}
        </div>
        ` : ''}

        ${!order.payment_split && order.payment_method === 'efectivo' && order.paid_with && order.paid_with > 0 ? `
        <div class="totals-section" style="margin-top: 1mm;">
          <div class="total-row">
            <span>Pagó con</span>
            <span>$${order.paid_with.toFixed(2)}</span>
          </div>
          <div class="total-row" style="font-weight: 800; font-size: 14px; padding-top: 0.5mm; border-top: 1px dashed #000;">
            <span>Cambio</span>
            <span>$${Math.max(0, order.paid_with - order.total).toFixed(2)}</span>
          </div>
        </div>
        ` : ''}
        
        ${order.is_courtesy ? `
        <div class="courtesy-stamp">
          *** CORTESÍA ***
        </div>
        ` : ''}
        
        ${order.vip_client_code ? `
        <div class="vip-stamp">
          <div class="vip-title">CLIENTE VIP</div>
          <div class="vip-name">${order.vip_client_name || ''}</div>
          <div class="vip-code">Código: ${order.vip_client_code}</div>
          ${order.vip_points_earned > 0 ? `<div class="vip-pts">+ ${order.vip_points_earned.toFixed(2)} pts ganados</div>` : ''}
          ${order.vip_points_redeemed > 0 ? `<div class="vip-pts">- ${order.vip_points_redeemed.toFixed(2)} pts canjeados</div>` : ''}
        </div>
        ` : ''}
        
        ${cleanNotes ? `
        <div class="divider">--------------------------------</div>
        <div class="notes-section">
          <span class="label">Notas:</span> ${cleanNotes}
        </div>
        ` : ''}
        
        <div class="divider">--------------------------------</div>
        
        <div class="footer">
          <div class="thanks">¡Gracias por tu compra!</div>
        </div>
        
        ${''}
        
        <script>
          window.onload = function() { 
            window.print(); 
            setTimeout(function() { window.close(); }, 500);
          }
        </script>
      </body>
      </html>
    `;
    
    // Use Blob URL instead of document.write to avoid XSS surface
    const blob = new Blob([ticketContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const printWindow = window.open(url, '_blank', 'width=350,height=600');
    if (printWindow) {
      // Revoke after the window had time to load
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } else {
      URL.revokeObjectURL(url);
      toast.error('No se pudo abrir la ventana de impresión. Permite popups.');
    }
  };

  // Manual order handlers
  const handleManualOrderChange = (field, value) => {
    setManualOrder(prev => ({ ...prev, [field]: value }));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...manualOrder.items];
    newItems[index] = { ...newItems[index], [field]: field === 'price' || field === 'quantity' ? Number(value) : value };
    setManualOrder(prev => ({ ...prev, items: newItems }));
  };

  const removeItem = (index) => {
    setManualOrder(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  // Calculate subtotal with per-item discounts applied
  const itemFinalPrice = (item) => {
    const disc = Number(item.discount_percent) || 0;
    return item.price * item.quantity * (1 - disc / 100);
  };
  
  const itemDiscountAmount = (item) => {
    const disc = Number(item.discount_percent) || 0;
    return item.price * item.quantity * disc / 100;
  };

  const manualSubtotalGross = useMemo(() => {
    return manualOrder.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  }, [manualOrder.items]);

  const manualPercentDiscount = useMemo(() => {
    return manualOrder.items.reduce((sum, item) => sum + itemDiscountAmount(item), 0);
  }, [manualOrder.items]);

  const manualSubtotal = useMemo(() => {
    return manualSubtotalGross - manualPercentDiscount;
  }, [manualSubtotalGross, manualPercentDiscount]);

  const manualVipDiscount = useMemo(() => {
    return Number(manualOrder.vip_points_to_redeem) || 0;
  }, [manualOrder.vip_points_to_redeem]);

  const manualTotal = useMemo(() => {
    const envio = manualOrder.delivery_type === 'domicilio' ? COSTO_ENVIO : 0;
    return Math.max(0, manualSubtotal + envio - manualVipDiscount);
  }, [manualSubtotal, manualOrder.delivery_type, manualVipDiscount]);

  const manualChargeTotal = manualOrder.invoice?.requires_invoice
    ? computeInvoice(manualTotal, manualOrder.invoice.apply_isr).total
    : manualTotal;

  const handleManualInvoiceChange = (next) => {
    const vipRfc = manualOrder.vip_client?.rfc;
    const vipRazon = manualOrder.vip_client?.razon_social;
    const turningOn = next.requires_invoice && !manualOrder.invoice?.requires_invoice;
    setManualOrder(prev => ({
      ...prev,
      invoice: turningOn
        ? { ...next, rfc: next.rfc || vipRfc || '', razon_social: next.razon_social || vipRazon || '' }
        : next
    }));
  };

  const manualPointsToEarn = useMemo(() => {
    if (!manualOrder.vip_client) return 0;
    return manualSubtotal * 0.05;
  }, [manualSubtotal, manualOrder.vip_client]);

  const isAllCourtesy = useMemo(() => {
    if (manualOrder.items.length === 0) return false;
    return manualOrder.items.every(item => Number(item.discount_percent) === 100);
  }, [manualOrder.items]);

  // Quick-register a walk-in customer as VIP with a referrer's code
  const quickRegisterVipWithReferrer = async ({ name, phone, address, referralCode, referrerName }) => {
    try {
      const createRes = await fetch(`${BACKEND_URL}/api/vip/clients`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          phone: phone.trim(),
          address: (address || '').trim() || 'Ventanilla'
        })
      });
      if (!createRes.ok) {
        const err = await createRes.json().catch(() => ({}));
        toast.error(err.detail || 'No se pudo registrar VIP');
        return;
      }
      const newVip = await createRes.json();
      const linkRes = await fetch(`${BACKEND_URL}/api/vip/clients/${newVip.id}/link-referrer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ referral_code: referralCode })
      });
      if (linkRes.ok) {
        toast.success(`✅ ${name} registrado VIP · ${referrerName} recibirá 2% en cada compra futura`);
        fetchVipClients();
      } else {
        const err = await linkRes.json().catch(() => ({}));
        toast.warning(`VIP creado pero no se pudo vincular: ${err.detail || 'error'}`);
      }
    } catch (e) {
      console.error(e);
      toast.error('Error al registrar VIP');
    }
  };

  const submitManualOrder = async (e, options = {}) => {
    if (e?.preventDefault) e.preventDefault();
    const { chargeAndPrint = false } = options;
    
    const isVentanilla = manualOrder.delivery_type === 'ventanilla';
    
    // Validate (relaxed for window orders)
    if (!isVentanilla) {
      if (!manualOrder.name || !manualOrder.phone) {
        toast.error('Ingresa nombre y teléfono del cliente');
        return;
      }
      if (!manualOrder.address) {
        toast.error('Ingresa la dirección de entrega');
        return;
      }
    }
    
    const validItems = manualOrder.items.filter(item => item.name && item.price > 0);
    if (validItems.length === 0) {
      toast.error('Agrega al menos un producto');
      return;
    }

    setIsSubmittingManual(true);

    try {
      const subtotalGross = validItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
      const totalItemDiscounts = validItems.reduce((sum, item) => {
        const disc = Number(item.discount_percent) || 0;
        return sum + (item.price * item.quantity * disc / 100);
      }, 0);
      const subtotalNet = subtotalGross - totalItemDiscounts;
      const envio = manualOrder.delivery_type === 'domicilio' ? COSTO_ENVIO : 0;
      const pointsToRedeem = Number(manualOrder.vip_points_to_redeem) || 0;
      const vipDiscount = manualOrder.vip_client ? pointsToRedeem : 0;
      const allCourtesy = validItems.length > 0 && validItems.every(it => Number(it.discount_percent) === 100);
      
      // Default values for ventanilla quick orders
      const customerName = manualOrder.name || (isVentanilla ? 'Ventanilla' : '');
      const customerPhone = manualOrder.phone || (isVentanilla ? '-' : '');
      
      const orderData = {
        name: customerName,
        phone: customerPhone,
        address: isVentanilla ? 'Recoger en ventanilla' : manualOrder.address,
        notes: (manualOrder.notes || '').trim() + (allCourtesy ? (manualOrder.notes ? ' · ' : '') + 'Cortesía' : ''),
        items: validItems.map((item, idx) => ({
          id: Date.now() + idx,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          notes: (item.notes || '').trim() || null,
          discount_percent: Number(item.discount_percent) || 0
        })),
        delivery_type: manualOrder.delivery_type,
        delivery_fee: envio,
        subtotal: subtotalNet,
        total: Math.max(0, subtotalNet + envio - vipDiscount),
        payment_method: manualOrder.payment_method,
        driver: manualOrder.delivery_type === 'domicilio' ? manualOrder.driver : null,
        vip_client_id: manualOrder.vip_client?.id || null,
        vip_client_code: manualOrder.vip_client?.code || null,
        vip_client_name: manualOrder.vip_client?.name || null,
        vip_points_redeemed: vipDiscount,
        vip_discount_applied: vipDiscount,
        discount_percent: 0,
        discount_amount: totalItemDiscounts,
        is_courtesy: allCourtesy,
        referral_code: (manualOrder.referral_code || '').trim().toUpperCase() || null,
        requires_invoice: !!manualOrder.invoice?.requires_invoice,
        invoice_rfc: manualOrder.invoice?.requires_invoice ? (manualOrder.invoice.rfc || null) : null,
        invoice_razon_social: manualOrder.invoice?.requires_invoice ? (manualOrder.invoice.razon_social || null) : null,
        invoice_apply_isr: !!(manualOrder.invoice?.requires_invoice && manualOrder.invoice.apply_isr)
      };

      const response = await fetch(`${BACKEND_URL}/api/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(orderData)
      });

      if (response.ok) {
        const createdOrder = await response.json();

        // Offer to register walk-in customer as VIP linked to referrer
        // (only when: no existing VIP, valid referral code, and we have name+phone)
        if (
          !manualOrder.vip_client &&
          manualOrder.referral_valid?.valid &&
          manualOrder.name?.trim() &&
          manualOrder.phone?.trim()
        ) {
          const snapshot = {
            name: manualOrder.name.trim(),
            phone: manualOrder.phone.trim(),
            address: manualOrder.address,
            referralCode: manualOrder.referral_code,
            referrerName: manualOrder.referral_valid.referrer_name
          };
          toast(`💫 ¿Registrar ${snapshot.name} como VIP con código de ${snapshot.referrerName}?`, {
            description: `${snapshot.referrerName} recibirá 2% en TODAS sus compras futuras (no solo esta).`,
            action: {
              label: 'Sí, registrar',
              onClick: () => quickRegisterVipWithReferrer(snapshot)
            },
            duration: 15000
          });
        }

        // If chargeAndPrint, open the Cobrar modal so cashier can enter "Paga con $"
        if (chargeAndPrint) {
          setOrderToCharge({ ...createdOrder, _selectedMethod: createdOrder.payment_method || 'efectivo' });
          setCustomerPaidWith('');
          setShowChargeModal(true);
          toast.success('¡Pedido creado! Ingresa el monto para cobrar');
        } else {
          toast.success('¡Pedido manual creado!');
        }
        
        // Reset form
        setManualOrder({
          name: '',
          phone: '',
          address: '',
          notes: '',
          delivery_type: 'ventanilla',
          payment_method: 'efectivo',
          driver: 'independiente',
          items: [],
          vip_client: null,
          vip_points_to_redeem: 0,
          referral_code: '',
          referral_valid: null,
          invoice: EMPTY_INVOICE
        });
        setManualPaidWith('');
        fetchData();
      } else {
        throw new Error('Error al crear pedido');
      }
    } catch (err) {
      console.error(err);
      toast.error('Error al crear el pedido');
    } finally {
      setIsSubmittingManual(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/orders/${orderId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });

      if (res.ok) {
        toast.success(`Estado actualizado a: ${STATUS_LABELS[newStatus]?.label}`);
        fetchData();
      }
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  // Cobrar - Always open modal so cashier can confirm/change payment method
  const markAsPaid = async (order) => {
    setOrderToCharge({ ...order, _selectedMethod: order.payment_method || 'efectivo' });
    setCustomerPaidWith('');
    setShowChargeModal(true);
  };

  const confirmMarkAsPaid = async (order, paymentMethodOverride, paidWithAmount, paymentSplit) => {
    try {
      const body = { payment_status: 'pagado' };
      if (paymentMethodOverride && paymentMethodOverride !== order.payment_method) {
        body.payment_method = paymentMethodOverride;
      }
      const finalMethod = paymentMethodOverride || order.payment_method;
      if (paymentSplit) {
        body.payment_split = paymentSplit;
      } else if (finalMethod === 'efectivo' && paidWithAmount && paidWithAmount > 0) {
        body.paid_with = paidWithAmount;
      }
      const res = await fetch(`${BACKEND_URL}/api/orders/${order.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (res.ok) {
        const updated = await res.json();
        toast.success('✅ Pedido COBRADO - Imprimiendo ticket...');
        printTicket(updated);
        fetchData();

        // If domicilio paid non-cash (or with insufficient cash), prompt a $50 driver cash-out
        if (updated.delivery_type === 'domicilio') {
          const sp = updated.payment_split || null;
          const cashPortion = sp
            ? Number(sp.cash || 0)
            : (updated.payment_method === 'efectivo' ? (Number(updated.total) || 0) : 0);
          const driverCashNeeded = Math.max(0, COSTO_ENVIO - cashPortion);
          if (driverCashNeeded > 0) {
            // Verify a cash register is open before offering the withdrawal
            try {
              const regRes = await fetch(`${BACKEND_URL}/api/cash-register/current`);
              const reg = regRes.ok ? await regRes.json() : null;
              if (reg) {
                setDriverCashOutPrompt({ order: updated, amount: driverCashNeeded });
              }
            } catch (_) { /* ignore */ }
          }
        }
      } else {
        let msg = `Error ${res.status}`;
        try { const err = await res.json(); msg = err.detail || err.message || msg; } catch (e) { /* ignore */ }
        toast.error(`❌ No se pudo cobrar: ${msg}`);
      }
    } catch (err) {
      console.error(err);
      toast.error('Error al marcar como pagado');
    }
  };

  const handleConfirmCharge = async () => {
    if (!orderToCharge) return;
    const order = orderToCharge;
    const total = Number(order.total) || 0;

    if (isSplitPayment) {
      const sc = Number(splitCash) || 0;
      const scard = Number(splitCard) || 0;
      const st = Number(splitTransfer) || 0;
      const sum = Number((sc + scard + st).toFixed(2));
      if (Math.abs(sum - total) > 0.01) {
        toast.error(`La suma del split ($${sum.toFixed(2)}) debe ser igual al total ($${total.toFixed(2)})`);
        return;
      }
      const nonZero = [['cash', sc], ['card', scard], ['transfer', st]].filter(([, v]) => v > 0);
      if (nonZero.length < 2) {
        toast.error('Para pago dividido necesitas al menos 2 métodos con monto');
        return;
      }
      const split = { cash: sc, card: scard, transfer: st };
      // Primary payment_method = largest portion (for legacy filters/labels)
      const primary = nonZero.sort((a, b) => b[1] - a[1])[0][0];
      const methodMap = { cash: 'efectivo', card: 'tarjeta', transfer: 'transferencia' };
      setShowChargeModal(false);
      setOrderToCharge(null);
      setIsSplitPayment(false);
      setSplitCash(''); setSplitCard(''); setSplitTransfer('');
      setCustomerPaidWith('');
      await confirmMarkAsPaid(order, methodMap[primary], null, split);
      return;
    }

    const method = order._selectedMethod || order.payment_method;
    const paidNum = parseFloat(customerPaidWith);
    const paidWithVal = !Number.isNaN(paidNum) && paidNum > 0 ? paidNum : null;
    setShowChargeModal(false);
    setOrderToCharge(null);
    setCustomerPaidWith('');
    await confirmMarkAsPaid(order, method, paidWithVal, null);
  };

  const confirmDriverCashOut = async () => {
    if (!driverCashOutPrompt) return;
    const { order, amount } = driverCashOutPrompt;
    try {
      const res = await fetch(`${BACKEND_URL}/api/cash-register/withdrawal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount,
          reason: `Pago a repartidor · Pedido #${order.id.slice(0, 8)}`
        })
      });
      if (res.ok) {
        toast.success(`✅ Retiro de $${amount.toFixed(2)} registrado · Abriendo cajón...`);
        openCashDrawer();
        fetchData();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(`No se pudo registrar el retiro: ${err.detail || 'error'}`);
      }
    } catch (e) {
      console.error(e);
      toast.error('Error al registrar retiro');
    } finally {
      setDriverCashOutPrompt(null);
    }
  };

  // Despejar - Permanently delete an order
  const cancelOrder = async (orderId) => {
    if (!window.confirm('¿Despejar este pedido? Se eliminará permanentemente.')) return;
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/orders/${orderId}`, {
        method: 'DELETE'
      });

      if (res.ok) {
        toast.success('Pedido eliminado');
        fetchData();
      } else {
        const error = await res.json().catch(() => ({}));
        toast.error(error.detail || 'Error al despejar pedido');
      }
    } catch (err) {
      console.error(err);
      toast.error('Error al despejar pedido');
    }
  };

  // Edit order - Start editing
  const startEditingOrder = (order) => {
    setEditingOrder({
      ...order,
      items: order.items.map(item => ({...item}))
    });
    fetchCatalog(); // Load catalog for adding items
  };

  // Update order items
  const updateOrderItem = (index, field, value) => {
    if (!editingOrder) return;
    const newItems = [...editingOrder.items];
    newItems[index] = { ...newItems[index], [field]: field === 'price' || field === 'quantity' ? Number(value) : value };
    
    const subtotal = newItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const deliveryFee = editingOrder.delivery_type === 'domicilio' ? COSTO_ENVIO : 0;
    
    setEditingOrder({
      ...editingOrder,
      items: newItems,
      subtotal: subtotal,
      total: subtotal + deliveryFee
    });
  };

  const addOrderItem = () => {
    if (!editingOrder) return;
    setEditingOrder({
      ...editingOrder,
      items: [...editingOrder.items, { name: '', price: 0, quantity: 1 }]
    });
  };

  const removeOrderItem = (index) => {
    if (!editingOrder || editingOrder.items.length <= 1) return;
    const newItems = editingOrder.items.filter((_, i) => i !== index);
    const subtotal = newItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const deliveryFee = editingOrder.delivery_type === 'domicilio' ? COSTO_ENVIO : 0;
    
    setEditingOrder({
      ...editingOrder,
      items: newItems,
      subtotal: subtotal,
      total: subtotal + deliveryFee
    });
  };

  const addCatalogItemToOrder = (catalogItem) => {
    if (!editingOrder) return;
    
    const existingIndex = editingOrder.items.findIndex(item => item.name === catalogItem.name);
    
    if (existingIndex >= 0) {
      // Increase quantity if already exists
      updateOrderItem(existingIndex, 'quantity', editingOrder.items[existingIndex].quantity + 1);
    } else {
      // Add new item
      const newItems = [...editingOrder.items.filter(item => item.name), {
        id: Date.now(),
        name: catalogItem.name,
        price: catalogItem.price,
        quantity: 1
      }];
      
      const subtotal = newItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
      const deliveryFee = editingOrder.delivery_type === 'domicilio' ? COSTO_ENVIO : 0;
      
      setEditingOrder({
        ...editingOrder,
        items: newItems,
        subtotal: subtotal,
        total: subtotal + deliveryFee
      });
    }
    toast.success(`+ ${catalogItem.name}`);
  };

  const saveOrderChanges = async () => {
    if (!editingOrder) return;
    
    const validItems = editingOrder.items.filter(item => item.name && item.price > 0);
    if (validItems.length === 0) {
      toast.error('Agrega al menos un producto');
      return;
    }

    try {
      const res = await fetch(`${BACKEND_URL}/api/orders/${editingOrder.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: validItems,
          subtotal: editingOrder.subtotal,
          total: editingOrder.total,
          notes: editingOrder.notes
        })
      });

      if (res.ok) {
        toast.success('Pedido actualizado');
        setEditingOrder(null);
        fetchData();
      }
    } catch (err) {
      console.error(err);
      toast.error('Error al guardar cambios');
    }
  };

  // Add catalog item to manual order
  const addCatalogItemToManualOrder = (catalogItem) => {
    const stock = menuStock[catalogItem.id];
    if (stock?.status === 'out' && !window.confirm(
      `⚠️ "${catalogItem.name}" está AGOTADO en inventario${stock.missing?.length ? ` (falta: ${stock.missing.join(', ')})` : ''}.\n\n¿Venderlo de todas formas?`
    )) return;
    const existingIndex = manualOrder.items.findIndex(item => item.name === catalogItem.name);
    
    if (existingIndex >= 0) {
      // Increase quantity
      handleItemChange(existingIndex, 'quantity', manualOrder.items[existingIndex].quantity + 1);
    } else {
      // Add new item
      setManualOrder(prev => ({
        ...prev,
        items: [...prev.items, { _key: `item-${Date.now()}-${Math.random()}`, name: catalogItem.name, price: catalogItem.price, quantity: 1, notes: '' }]
      }));
    }
    toast.success(`+ ${catalogItem.name}`, { duration: 1000 });
  };

  // Send order confirmation to WhatsApp
  const sendOrderToWhatsApp = (order) => {
    // Build the message
    const items = order.items?.map(item => {
      const line = `• ${item.quantity}x ${item.name} - $${(item.price * item.quantity).toFixed(2)}`;
      return item.notes && item.notes.trim() ? `${line}\n    _${item.notes.trim()}_` : line;
    }).join('\n') || '';
    
    const message = `🍗 *MR. CRUNCH 28*
━━━━━━━━━━━━━━━
*Confirmación de Pedido*
Pedido #${order.id?.slice(0,8)}

👤 *Cliente:* ${order.customer_name}
📱 *Tel:* ${order.customer_phone}
${order.delivery_type === 'domicilio' ? `📍 *Dirección:* ${order.customer_address}` : '🏪 *Tipo:* Recoger en ventanilla'}

━━━━━━━━━━━━━━━
*PRODUCTOS:*
${items}
━━━━━━━━━━━━━━━
${order.delivery_type === 'domicilio' ? `Subtotal: $${(order.subtotal || (order.total - 50)).toFixed(2)}
Envío: $50.00
` : ''}*TOTAL: $${order.total?.toFixed(2)} MXN*

💳 Pago: ${paymentLabel(order.payment_method)}
${order.notes ? `\n📝 Notas: ${order.notes}` : ''}

━━━━━━━━━━━━━━━
¿Tu pedido está correcto? 
Responde *SÍ* para confirmar ✅`;

    // Clean phone number
    let phone = order.customer_phone?.replace(/\D/g, '') || '';
    if (phone.length === 10) {
      phone = '52' + phone; // Add Mexico country code
    }
    
    const whatsappUrl = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
    toast.success('Abriendo WhatsApp...');
  };

  // Copy current manual order as confirmation message (before submitting)
  const copyManualOrderForConfirmation = () => {
    const validItems = manualOrder.items.filter(item => item.name && item.price > 0);
    if (validItems.length === 0) {
      toast.error('Agrega al menos un producto antes de copiar');
      return;
    }
    
    const subtotal = manualSubtotal;
    const envio = manualOrder.delivery_type === 'domicilio' ? COSTO_ENVIO : 0;
    const items = validItems.map(item => {
      const line = `• ${item.quantity}x ${item.name} - $${(item.price * item.quantity).toFixed(2)}`;
      return item.notes && item.notes.trim() ? `${line}\n    _${item.notes.trim()}_` : line;
    }).join('\n');
    
    const text = `🍗 *MR. CRUNCH 28*
━━━━━━━━━━━━━━━
*Confirmación de Pedido*

👤 *Cliente:* ${manualOrder.name || '(sin nombre)'}
📱 *Tel:* ${manualOrder.phone || '(sin teléfono)'}
${manualOrder.delivery_type === 'domicilio'
  ? `📍 *Dirección:* ${manualOrder.address || '(sin dirección)'}`
  : '🏪 *Tipo:* Recoger en ventanilla'}

━━━━━━━━━━━━━━━
*PRODUCTOS:*
${items}
━━━━━━━━━━━━━━━
${manualOrder.delivery_type === 'domicilio' ? `Subtotal: $${subtotal.toFixed(2)}
Envío: $${envio.toFixed(2)}
` : ''}${manualPercentDiscount > 0 ? `Descuento ${manualOrder.discount_percent === 100 ? 'CORTESÍA' : `${manualOrder.discount_percent}%`}: -$${manualPercentDiscount.toFixed(2)}
` : ''}${manualVipDiscount > 0 ? `Descuento VIP: -$${manualVipDiscount.toFixed(2)}
` : ''}*TOTAL: $${manualTotal.toFixed(2)} MXN*${manualOrder.notes ? `\n\n📝 *Notas:* ${manualOrder.notes}` : ''}

━━━━━━━━━━━━━━━
✅ ¿Tu pedido está correcto?
Responde *SÍ* para confirmar.

💳 ¿Cómo deseas pagar?
• *Efectivo*
• *Transferencia*`;

    navigator.clipboard.writeText(text).then(() => {
      toast.success('📋 Ticket copiado — envíaselo al cliente');
    }).catch((err) => {
      console.error(err);
      toast.error('Error al copiar');
    });
  };

  // Copy short message for external delivery driver
  const copyManualOrderForDriver = () => {
    const validItems = manualOrder.items.filter(item => item.name && item.price > 0);
    if (validItems.length === 0) {
      toast.error('Agrega al menos un producto antes de copiar');
      return;
    }
    if (manualOrder.delivery_type !== 'domicilio') {
      toast.error('Esta opción es solo para pedidos a domicilio');
      return;
    }
    if (!manualOrder.address || !manualOrder.address.trim()) {
      toast.error('Agrega la dirección del cliente primero');
      return;
    }

    const text = `🍗 *Pedido Mr Crunch 28*

📍 *Dirección:* ${manualOrder.address.trim()}${manualOrder.phone ? `\n📱 *Tel cliente:* ${manualOrder.phone}` : ''}

💰 *Total a cobrar (con envío): $${manualTotal.toFixed(2)} MXN*`;

    navigator.clipboard.writeText(text).then(() => {
      toast.success('🛵 Mensaje copiado — envíaselo al repartidor');
    }).catch((err) => {
      console.error(err);
      toast.error('Error al copiar');
    });
  };

  // Copy order to clipboard - Confirmation message style
  const copyOrderToClipboard = (order) => {
    const items = order.items?.map(item => {
      const line = `• ${item.quantity}x ${item.name} - $${(item.price * item.quantity).toFixed(2)}`;
      return item.notes && item.notes.trim() ? `${line}\n    _${item.notes.trim()}_` : line;
    }).join('\n') || '';
    
    const text = `🍗 *MR. CRUNCH 28*
━━━━━━━━━━━━━━━
*Confirmación de Pedido*
Pedido #${order.id?.slice(0,8)}

👤 *Cliente:* ${order.customer_name}
📱 *Tel:* ${order.customer_phone}
${order.delivery_type === 'domicilio' ? `📍 *Dirección:* ${order.customer_address}` : '🏪 *Tipo:* Recoger en ventanilla'}

━━━━━━━━━━━━━━━
*PRODUCTOS:*
${items}
━━━━━━━━━━━━━━━
${order.delivery_type === 'domicilio' ? `Subtotal: $${(order.subtotal || (order.total - 50)).toFixed(2)}
Envío: $50.00
` : ''}*TOTAL: $${order.total?.toFixed(2)} MXN*${order.notes ? `\n\n📝 *Notas:* ${order.notes}` : ''}

━━━━━━━━━━━━━━━
✅ ¿Tu pedido está correcto?
Responde *SÍ* para confirmar.

💳 ¿Cómo deseas pagar?
• *Efectivo*
• *Transferencia*`;

    navigator.clipboard.writeText(text).then(() => {
      toast.success('📋 Pedido copiado al portapapeles');
    }).catch((err) => {
      console.error(err);
      toast.error('Error al copiar');
    });
  };

  // Build a personalized marketing message for a given VIP client
  const buildVipMarketingMessage = (client) => {
    const points = Number(client.points) || 0;
    const firstName = (client.name || '').trim().split(' ')[0] || 'cliente';

    let bodyMsg = '';
    if (points >= 100) {
      bodyMsg = `Te queremos consentir 👑\n\nTienes acumulados *${points.toFixed(0)} puntos* en Mr. Crunch 28 — eso equivale a *$${points.toFixed(0)} MXN* de descuento directo en tu próximo pedido. ¡No los dejes guardados!\n\n🎁 *Oferta especial esta semana:* Además de aplicar tus puntos, te regalamos un *10% extra* en tu próxima compra. 😋`;
    } else if (points >= 30) {
      bodyMsg = `Sigues siendo parte de nuestra familia VIP 👑\n\nTienes *${points.toFixed(0)} puntos* listos para canjear ($${points.toFixed(0)} MXN de descuento). Combínalos con un combo especial y la pasas increíble.\n\n🎁 *Bonus:* Pide esta semana y te sumamos *puntos dobles* en tu compra.`;
    } else if (points > 0) {
      bodyMsg = `¡Te extrañamos! 👋\n\nTienes *${points.toFixed(0)} puntos* acumulados. Por cada compra ganas un 5% en puntos, así que vente a comer y los duplicamos rapidísimo.\n\n🎁 *Esta semana:* Te regalamos una *orden chica de papas* en pedidos mayores a $200.`;
    } else {
      bodyMsg = `¡Hola! 👋\n\nQueremos verte de nuevo en *Mr. Crunch 28* 🍗. Por cada compra acumulas el 5% en puntos VIP — y los puedes usar como descuento cuando quieras.\n\n🎁 *Promo de la semana:* Combo familiar con *10% de descuento* solo para clientes VIP.`;
    }

    return `🍗 *MR. CRUNCH 28* 👑

Hola ${firstName}, ${bodyMsg}

📲 Solo escríbenos por aquí para hacer tu pedido y aplicamos tu descuento al momento.

¡Te esperamos! 🔥`;
  };

  // Normalize a phone to digits-only with country code (MX +52)
  const normalizePhoneForWhatsApp = (phone) => {
    const digits = String(phone || '').replace(/\D/g, '');
    if (!digits) return '';
    // Already has country code (≥11 digits starting with 52)
    if (digits.length >= 12 && digits.startsWith('52')) return digits;
    if (digits.length === 10) return `52${digits}`;
    return digits;
  };

  // Generate and copy a marketing/invitation message for a VIP customer
  const copyVipMarketing = (client) => {
    const firstName = (client.name || '').trim().split(' ')[0] || 'cliente';
    const msg = buildVipMarketingMessage(client);
    navigator.clipboard.writeText(msg).then(() => {
      toast.success(`📋 Invitación copiada — envíasela a ${firstName} por WhatsApp`);
    }).catch((err) => {
      console.error(err);
      toast.error('Error al copiar');
    });
  };

  // Copy all VIP marketing messages as a single block (with separators) for bulk sending
  const copyAllVipMarketing = () => {
    if (!vipClients || vipClients.length === 0) {
      toast.error('No hay clientes VIP para invitar');
      return;
    }
    const separator = '\n\n━━━━━━━━━━━━━━━━━━━━━━\n\n';
    const allMessages = vipClients.map(c => {
      const phoneTag = c.phone ? `📱 Para: ${c.phone}` : '';
      return `${phoneTag}\n\n${buildVipMarketingMessage(c)}`;
    }).join(separator);
    navigator.clipboard.writeText(allMessages).then(() => {
      toast.success(`📋 ${vipClients.length} mensajes copiados al portapapeles`);
    }).catch((err) => {
      console.error(err);
      toast.error('Error al copiar');
    });
  };

  // Manually adjust VIP points (for corrections / fixes)
  const adjustVipPoints = async (client) => {
    const input = window.prompt(
      `Ajustar puntos de ${client.name}\nPuntos actuales: ${client.points?.toFixed(2) || '0.00'}\n\nIngresa cantidad (positivo para sumar, negativo para restar):`,
      ''
    );
    if (input === null || input.trim() === '') return;
    const delta = parseFloat(input);
    if (isNaN(delta)) {
      toast.error('Cantidad inválida');
      return;
    }
    const reason = window.prompt('Motivo del ajuste (opcional):', '') || '';
    
    try {
      const url = `${BACKEND_URL}/api/vip/clients/${client.id}/adjust-points?delta=${delta}&reason=${encodeURIComponent(reason)}`;
      const res = await fetch(url, { method: 'POST' });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Puntos: ${data.previous_points.toFixed(2)} → ${data.new_points.toFixed(2)}`);
        fetchVipClients();
      } else {
        const error = await res.json().catch(() => ({}));
        toast.error(error.detail || 'Error al ajustar puntos');
      }
    } catch (err) {
      console.error(err);
      toast.error('Error al ajustar puntos');
    }
  };

  // Reembolsar - Refund order
  const refundOrder = async (orderId) => {
    const reason = window.prompt('¿Motivo del reembolso?');
    if (reason === null) return; // User cancelled
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/orders/${orderId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          payment_status: 'reembolsado',
          refund_reason: reason || 'Sin especificar',
          status: 'cancelado'
        })
      });

      if (res.ok) {
        toast.success('💸 Pedido REEMBOLSADO');
        fetchData();
      }
    } catch (err) {
      console.error(err);
      toast.error('Error al reembolsar');
    }
  };

  // Pedidos pendientes del día (no cobrados, no reembolsados)
  const pendingOrders = todayOrders.filter(o => 
    o.payment_status !== 'pagado' && o.payment_status !== 'reembolsado'
  );

  // Pedidos cobrados del día
  const paidOrders = todayOrders.filter(o => o.payment_status === 'pagado');

  // Pedidos reembolsados
  const refundedOrders = orders.filter(o => o.payment_status === 'reembolsado');

  // Filtrar según status dentro de cada categoría
  const filteredPendingOrders = statusFilter === 'all' 
    ? pendingOrders 
    : pendingOrders.filter(o => o.status === statusFilter);

  const filteredPaidOrders = statusFilter === 'all'
    ? paidOrders
    : paidOrders.filter(o => o.status === statusFilter);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Clock className="h-16 w-16 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-black to-gray-900">
      <Toaster position="top-center" richColors />
      
      {/* Header */}
      <header className="bg-black/80 backdrop-blur-md border-b border-gray-800 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-2">
            <h1 className="text-white font-bold text-xl">Panel de Administración</h1>
            <div className="flex items-center gap-2">
              <Button
                onClick={openCashDrawer}
                size="sm"
                className="bg-emerald-700 hover:bg-emerald-600 text-white"
                data-testid="btn-open-drawer"
                title="Abrir cajón de dinero (imprime mini-ticket de apertura)"
              >
                <Wallet className="h-4 w-4 mr-1" /> Abrir Cajón
              </Button>
              <Button onClick={fetchData} variant="outline" size="sm">
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="border-b border-gray-800">
        <div className="container mx-auto px-4">
          <div className="flex space-x-2 md:space-x-4 overflow-x-auto">
            {['orders', 'cobrados', 'manual', 'catalogo', 'inventario', 'vip', 'referrals', 'facturacion', 'caja', 'recibos'].map(tab => (
              <button
                key={tab}
                onClick={() => {
                  setActiveTab(tab);
                  if (tab === 'caja') fetchCashHistory();
                  if (tab === 'catalogo' || tab === 'manual') fetchCatalog();
                  if (tab === 'vip' || tab === 'manual') fetchVipClients();
                  if (tab === 'recibos') fetchReceipts();
                  if (tab === 'referrals') { fetchReferralsStats(); fetchReferralsList(); fetchReferralConfig(); }
                }}
                className={`py-4 px-4 md:px-6 font-medium transition-all border-b-2 whitespace-nowrap ${
                  activeTab === tab
                    ? 'text-orange-500 border-orange-500'
                    : 'text-gray-400 border-transparent hover:text-white'
                }`}
                data-testid={`tab-${tab}`}
              >
                {tab === 'orders' ? '🔴 Pendientes' : 
                 tab === 'cobrados' ? '✅ Cobrados' :
                 tab === 'manual' ? '+ Nuevo' :
                 tab === 'catalogo' ? '📋 Catálogo' :
                 tab === 'inventario' ? (
                   <span className="inline-flex items-center gap-1.5">📦 Inventario{lowStockCount > 0 && (
                     <span className="bg-amber-500 text-black text-[10px] font-bold rounded-full px-1.5" data-testid="inventory-low-badge">{lowStockCount}</span>
                   )}</span>
                 ) :
                 tab === 'vip' ? '👑 VIP' :
                 tab === 'referrals' ? '🎁 Recomendaciones' :
                 tab === 'facturacion' ? '🧾 Facturación' :
                 tab === 'caja' ? '💰 Caja' :
                 tab === 'recibos' ? '📜 Recibos' :
                 ''}
              </button>
            ))}
          </div>
        </div>
      </div>

      <main className="container mx-auto px-4 py-6">
        {/* Manual Order Tab */}
        {activeTab === 'manual' && (
          <div className="grid md:grid-cols-5 gap-6">
            {/* Left - Visual Catalog */}
            <div className="md:col-span-3 md:order-1">
              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-white font-bold text-lg flex items-center">
                    <ShoppingBag className="h-5 w-5 mr-2 text-orange-500" />
                    {manualCatalogFilter ? manualCatalogFilter : 'Categorías'}
                  </h3>
                  {manualCatalogFilter && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => { setManualCatalogFilter(''); setManualCatalogSearch(''); }}
                      className="border-gray-700 text-gray-300 hover:bg-gray-800 h-8"
                      data-testid="back-to-categories-btn"
                    >
                      <ArrowLeft className="h-4 w-4 mr-1" /> Categorías
                    </Button>
                  )}
                </div>

                {/* Search bar */}
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Buscar producto en todo el menú..."
                    value={manualCatalogSearch}
                    onChange={(e) => setManualCatalogSearch(e.target.value)}
                    className="pl-9 bg-black/50 border-gray-700 text-white text-sm h-10"
                    data-testid="manual-catalog-search"
                  />
                </div>

                {/* Category grid (visible when no category selected and no search) */}
                {!manualCatalogFilter && !manualCatalogSearch.trim() && (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="category-grid">
                    {MENU_CATEGORIES.map(cat => {
                      const visual = CATEGORY_VISUALS[cat] || { icon: ShoppingBag, color: 'from-gray-600 to-gray-800', ring: 'ring-gray-400' };
                      const Icon = visual.icon;
                      const count = catalog[cat]?.filter(i => i.available !== false).length || 0;
                      return (
                        <button
                          key={cat}
                          type="button"
                          onClick={() => setManualCatalogFilter(cat)}
                          disabled={count === 0}
                          className={`group relative rounded-xl bg-gradient-to-br ${visual.color} p-2.5 flex flex-col items-center justify-center text-white shadow-md transition-all duration-200 border border-white/10 min-h-[88px] ${
                            count === 0 ? 'opacity-40 cursor-not-allowed' : 'hover:scale-[1.04] hover:shadow-lg hover:border-white/30'
                          }`}
                          data-testid={`category-btn-${cat}`}
                        >
                          <div className="bg-black/25 backdrop-blur-sm rounded-full p-1.5 mb-1 group-hover:bg-black/35 transition-colors">
                            <Icon className="h-4 w-4" strokeWidth={2.2} />
                          </div>
                          <span className="text-[11px] font-bold text-center leading-tight line-clamp-2">{cat}</span>
                          <span className="text-[9px] opacity-75 mt-0.5">{count}</span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Products grid (when category selected or searching) */}
                {(manualCatalogFilter || manualCatalogSearch.trim()) && (
                  <div className="space-y-4">
                    {MENU_CATEGORIES
                      .filter(c => !manualCatalogFilter || manualCatalogFilter === c)
                      .map(category => {
                        const allItems = catalog[category] || [];
                        const items = (manualCatalogSearch.trim()
                          ? allItems.filter(it =>
                              it.name?.toLowerCase().includes(manualCatalogSearch.toLowerCase()) ||
                              it.description?.toLowerCase().includes(manualCatalogSearch.toLowerCase())
                            )
                          : allItems
                        ).filter(item => item.available !== false);

                        if (items.length === 0) return null;
                        const visual = CATEGORY_VISUALS[category] || { icon: ShoppingBag, color: 'from-gray-600 to-gray-800' };
                        const Icon = visual.icon;

                        return (
                          <div key={category}>
                            {!manualCatalogFilter && (
                              <p className="text-gray-400 text-xs uppercase tracking-wide mb-2 font-bold flex items-center gap-2">
                                <Icon className="h-4 w-4" /> {category}
                              </p>
                            )}
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                              {items.map((item, idx) => {
                                const canReorder = false;
                                const positionInCat = (catalog[category] || []).findIndex(i => i.id === item.id);
                                const catLen = (catalog[category] || []).length;
                                const isFirst = positionInCat <= 0;
                                const isLast = positionInCat >= catLen - 1;
                                return (
                                <div key={item.id} className="relative">
                                  <button
                                    type="button"
                                    onClick={() => addCatalogItemToManualOrder(item)}
                                    className={`relative w-full p-3 bg-gradient-to-br ${visual.color} bg-opacity-20 hover:scale-[1.02] rounded-xl transition-all text-left border-2 border-white/10 hover:border-white/30 shadow-md hover:shadow-lg group`}
                                    data-testid={`catalog-item-${item.id}`}
                                  >
                                    <div className="flex items-start gap-2 mb-1">
                                      <Icon className="h-4 w-4 text-white/80 shrink-0 mt-0.5" />
                                      <span className="text-white font-semibold text-sm leading-tight line-clamp-2">{item.name}</span>
                                    </div>
                                    <div className="text-white text-base font-bold mt-1">${item.price?.toFixed(2)}</div>
                                    {(menuStock[item.id]?.status === 'out' || menuStock[item.id]?.status === 'low') && (
                                      <span
                                        className={`absolute top-1 right-1 text-[10px] font-bold px-1.5 py-0.5 rounded ${menuStock[item.id].status === 'out' ? 'bg-red-600 text-white' : 'bg-amber-400 text-black'}`}
                                        data-testid={`catalog-stock-badge-${item.id}`}
                                      >
                                        {menuStock[item.id].status === 'out' ? 'AGOTADO' : 'STOCK BAJO'}
                                      </span>
                                    )}
                                  </button>
                                  {canReorder && (
                                    <div className="absolute top-1 right-1 flex gap-0.5 bg-black/30 backdrop-blur-sm rounded-md border border-white/10 z-10">
                                      <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); moveCatalogItem(category, item.id, 'up'); }}
                                        disabled={isFirst}
                                        className={`p-0.5 rounded-sm ${isFirst ? 'text-white/20 cursor-not-allowed' : 'text-white/90 hover:bg-white/20'}`}
                                        title="Mover antes"
                                        data-testid={`manual-reorder-up-${item.id}`}
                                      >
                                        <ChevronUp className="h-3.5 w-3.5" strokeWidth={2.5} />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); moveCatalogItem(category, item.id, 'down'); }}
                                        disabled={isLast}
                                        className={`p-0.5 rounded-sm ${isLast ? 'text-white/20 cursor-not-allowed' : 'text-white/90 hover:bg-white/20'}`}
                                        title="Mover después"
                                        data-testid={`manual-reorder-down-${item.id}`}
                                      >
                                        <ChevronDown className="h-3.5 w-3.5" strokeWidth={2.5} />
                                      </button>
                                    </div>
                                  )}
                                </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}

                    {(() => {
                      const totalVisible = MENU_CATEGORIES
                        .filter(c => !manualCatalogFilter || manualCatalogFilter === c)
                        .reduce((sum, c) => {
                          const allItems = catalog[c] || [];
                          const items = (manualCatalogSearch.trim()
                            ? allItems.filter(it =>
                                it.name?.toLowerCase().includes(manualCatalogSearch.toLowerCase()) ||
                                it.description?.toLowerCase().includes(manualCatalogSearch.toLowerCase())
                              )
                            : allItems
                          ).filter(item => item.available !== false);
                          return sum + items.length;
                        }, 0);
                      if (totalVisible === 0) {
                        return (
                          <div className="text-center py-8 text-gray-500">
                            <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
                            <p>Sin resultados</p>
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                )}

                {Object.values(catalog).every(items => items.length === 0) && (
                  <div className="text-center py-8 text-gray-500">
                    <ShoppingBag className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No hay productos en el catálogo</p>
                    <p className="text-sm">Ve a la pestaña Catálogo para agregar</p>
                  </div>
                )}
              </div>

              {/* Selected products (moved from right column for cleaner layout) */}
              <div className="mt-4 bg-gray-900/50 border border-gray-800 rounded-xl p-3">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-white font-semibold text-sm flex items-center gap-2">
                    <ShoppingBag className="h-4 w-4 text-orange-500" />
                    Productos seleccionados
                  </h3>
                  {manualOrder.items.filter(i => i.name).length > 0 && (
                    <span className="text-gray-500 text-xs">{manualOrder.items.filter(i => i.name).length} item(s)</span>
                  )}
                </div>
                {manualOrder.items.filter(i => i.name).length === 0 ? (
                  <div className="text-center py-3 text-gray-500 border-2 border-dashed border-gray-700 rounded-lg">
                    <p className="text-xs">Click en un producto del catálogo arriba para agregarlo</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {manualOrder.items.map((item, index) => {
                      if (!item.name) return null;
                      const itemDisc = Number(item.discount_percent) || 0;
                      const itemFinal = item.price * item.quantity * (1 - itemDisc / 100);
                      const showPicker = discountPickerFor === index;
                      return (
                        <div
                          key={item._key || `idx-${index}`}
                          className={`bg-black/30 p-2 rounded-lg ${itemDisc > 0 ? 'border border-purple-500/40' : ''}`}
                        >
                          <div className="flex gap-2 items-center">
                            <div className="flex-1 min-w-0">
                              <p className="text-white font-medium truncate text-sm">
                                {item.name}
                                {itemDisc > 0 && (
                                  <span className="ml-2 bg-purple-500 text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold">
                                    {itemDisc === 100 ? 'CORTESÍA' : `-${itemDisc}%`}
                                  </span>
                                )}
                              </p>
                              <p className="text-gray-500 text-xs">
                                ${Number(item.price).toFixed(2)} c/u
                                {itemDisc > 0 && (
                                  <span className="text-purple-400 ml-1">→ ${(item.price * (1 - itemDisc/100)).toFixed(2)}</span>
                                )}
                              </p>
                            </div>
                            <div className="flex items-center bg-gray-800 rounded-lg">
                              <button
                                type="button"
                                onClick={() => handleItemChange(index, 'quantity', Math.max(1, Number(item.quantity) - 1))}
                                className="w-8 h-8 flex items-center justify-center text-gray-300 hover:bg-gray-700 rounded-l-lg"
                              >−</button>
                              <span className="w-10 text-center text-white font-bold">{item.quantity}</span>
                              <button
                                type="button"
                                onClick={() => handleItemChange(index, 'quantity', Number(item.quantity) + 1)}
                                className="w-8 h-8 flex items-center justify-center text-gray-300 hover:bg-gray-700 rounded-r-lg"
                              >+</button>
                            </div>
                            <p className="text-green-400 font-semibold w-20 text-right">${itemFinal.toFixed(2)}</p>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => setDiscountPickerFor(showPicker ? null : index)}
                              className={`h-8 w-8 ${itemDisc > 0 ? 'text-purple-400 bg-purple-500/10' : 'text-gray-400 hover:bg-purple-500/10'}`}
                              title="Descuento"
                            >
                              <Gift className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => removeItem(index)}
                              className="text-red-500 hover:bg-red-500/10 h-8 w-8"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                          {showPicker && (
                            <div className="mt-2 pt-2 border-t border-gray-700 flex flex-wrap gap-1.5 items-center">
                              <span className="text-xs text-gray-400 mr-1">Descuento:</span>
                              {[0, 5, 10, 15, 50, 100].map(val => {
                                const active = itemDisc === val;
                                const label = val === 0 ? 'Sin desc' : val === 100 ? 'CORTESÍA' : `${val}%`;
                                return (
                                  <button
                                    key={val}
                                    type="button"
                                    onClick={() => {
                                      handleItemChange(index, 'discount_percent', val);
                                      if (val === 0) setDiscountPickerFor(null);
                                    }}
                                    className={`px-2 py-1 rounded text-xs font-bold transition-all ${
                                      active
                                        ? val === 100 ? 'bg-pink-500 text-white' : val === 0 ? 'bg-gray-600 text-white' : 'bg-purple-500 text-white'
                                        : 'bg-black/40 border border-gray-700 text-gray-300 hover:bg-purple-900/20'
                                    }`}
                                  >
                                    {label}
                                  </button>
                                );
                              })}
                              <button
                                type="button"
                                onClick={() => setDiscountPickerFor(null)}
                                className="ml-auto text-gray-500 hover:text-white px-2 py-1 text-xs"
                              >
                                Cerrar
                              </button>
                            </div>
                          )}
                          {/* Per-item special instructions */}
                          <div className="mt-1.5">
                            <Input
                              value={item.notes || ''}
                              onChange={(e) => handleItemChange(index, 'notes', e.target.value)}
                              placeholder="Especificación (ej: sin cebolla, extra picante)"
                              className="bg-black/40 border-gray-700 text-yellow-200 text-xs h-7 placeholder:text-gray-600"
                              data-testid={`manual-item-notes-${index}`}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Right - Order Form */}
            <div className="md:col-span-2 md:order-2">
            <form onSubmit={submitManualOrder} className="space-y-2">
              {/* Customer Info */}
              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-2">
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    value={manualOrder.name}
                    onChange={(e) => handleManualOrderChange('name', e.target.value)}
                    placeholder={manualOrder.delivery_type === 'ventanilla' ? 'Nombre (opcional)' : 'Nombre del cliente'}
                    className="bg-black/50 border-gray-700 text-white h-8 text-sm"
                  />
                  <Input
                    value={manualOrder.phone}
                    onChange={(e) => handleManualOrderChange('phone', e.target.value)}
                    placeholder={manualOrder.delivery_type === 'ventanilla' ? 'Tel (opcional)' : '877 123 4567'}
                    className="bg-black/50 border-gray-700 text-white h-8 text-sm"
                  />
                </div>
              </div>

              {/* VIP Client - compact */}
              <div className="bg-gradient-to-r from-yellow-900/20 to-amber-900/20 border border-yellow-500/30 rounded-xl p-2">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="text-white font-semibold flex items-center text-xs">
                    <Crown className="h-3.5 w-3.5 mr-1.5 text-yellow-500" />
                    Cliente VIP
                  </h3>
                  {!manualOrder.vip_client && (
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => setShowVipSearch(true)}
                      className="bg-yellow-600 hover:bg-yellow-700 h-7 text-xs"
                      data-testid="btn-search-vip"
                    >
                      <Search className="h-3 w-3 mr-1" /> Buscar
                    </Button>
                  )}
                </div>
                
                {manualOrder.vip_client && (
                  <div className="bg-black/30 rounded-lg p-2 border border-yellow-500/20 mt-2">
                    <div className="flex justify-between items-center gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Star className="h-4 w-4 text-yellow-400 shrink-0" />
                          <span className="text-yellow-400 font-bold truncate">{manualOrder.vip_client.name}</span>
                          <span className="font-mono text-white text-xs bg-black/40 px-2 py-0.5 rounded">{manualOrder.vip_client.code}</span>
                          <span className="text-green-400 text-sm font-bold">
                            {manualOrder.vip_client.points?.toFixed(2) || '0.00'} pts
                          </span>
                        </div>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={removeVipFromOrder}
                        className="text-red-400 hover:bg-red-900/20 h-7 w-7 p-0 shrink-0"
                        data-testid="btn-remove-vip"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    
                    {manualOrder.vip_client.points > 0 && (
                      <div className="flex items-center gap-2 mt-2">
                        <Gift className="h-4 w-4 text-yellow-500 shrink-0" />
                        <span className="text-gray-400 text-xs whitespace-nowrap">Canjear:</span>
                        <Input
                          type="number"
                          min="0"
                          max={Math.min(manualOrder.vip_client.points, manualSubtotal)}
                          step="0.01"
                          value={manualOrder.vip_points_to_redeem}
                          onChange={(e) => {
                            const val = Math.min(
                              Number(e.target.value) || 0,
                              manualOrder.vip_client.points,
                              manualSubtotal
                            );
                            setManualOrder(prev => ({ ...prev, vip_points_to_redeem: val }));
                          }}
                          className="bg-black/50 border-gray-700 text-white h-8 text-sm flex-1"
                          data-testid="input-redeem-points"
                        />
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => setManualOrder(prev => ({
                            ...prev,
                            vip_points_to_redeem: Math.min(prev.vip_client.points, manualSubtotal)
                          }))}
                          className="border-yellow-500/40 text-yellow-400 h-8 px-2 text-xs"
                        >
                          Máx
                        </Button>
                        {manualSubtotal > 0 && (
                          <span className="text-yellow-400 text-xs whitespace-nowrap">
                            +{manualPointsToEarn.toFixed(0)} pts
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Delivery Type */}
              <div>
                {/* Tipo de Entrega */}
                <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-2">
                  <div className="grid grid-cols-2 gap-1.5">
                    <button
                      type="button"
                      onClick={() => handleManualOrderChange('delivery_type', 'ventanilla')}
                      className={`px-2 py-2 rounded-lg border-2 transition-all flex items-center justify-center gap-1.5 text-xs font-semibold ${
                        manualOrder.delivery_type === 'ventanilla'
                          ? 'border-orange-500 bg-orange-500/20 text-orange-500'
                          : 'border-gray-700 bg-black/30 text-gray-400'
                      }`}
                    >
                      <Store className="h-3.5 w-3.5" />
                      Ventanilla
                    </button>
                    <button
                      type="button"
                      onClick={() => handleManualOrderChange('delivery_type', 'domicilio')}
                      className={`px-2 py-2 rounded-lg border-2 transition-all flex items-center justify-center gap-1.5 text-xs font-semibold ${
                        manualOrder.delivery_type === 'domicilio'
                          ? 'border-orange-500 bg-orange-500/20 text-orange-500'
                          : 'border-gray-700 bg-black/30 text-gray-400'
                      }`}
                    >
                      <Truck className="h-3.5 w-3.5" />
                      Domicilio +$50
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Address + Driver (only for domicilio, in single row) */}
              {manualOrder.delivery_type === 'domicilio' && (
                <div className="grid md:grid-cols-2 gap-2">
                  <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-2">
                    <Textarea
                      value={manualOrder.address}
                      onChange={(e) => handleManualOrderChange('address', e.target.value)}
                      placeholder="Dirección: calle, número, colonia"
                      className="bg-black/50 border-gray-700 text-white text-sm"
                      rows={1}
                    />
                  </div>
                  <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-2">
                    <div className="grid grid-cols-2 gap-1.5">
                      <button
                        type="button"
                        onClick={() => handleManualOrderChange('driver', 'antoni')}
                        className={`px-2 py-2 rounded-lg border-2 transition-all flex items-center justify-center gap-1.5 text-xs font-semibold ${
                          manualOrder.driver === 'antoni'
                            ? 'border-emerald-500 bg-emerald-500/20 text-emerald-400'
                            : 'border-gray-700 bg-black/30 text-gray-400'
                        }`}
                        data-testid="driver-antoni"
                      >
                        <Truck className="h-3.5 w-3.5" /> Antoni
                      </button>
                      <button
                        type="button"
                        onClick={() => handleManualOrderChange('driver', 'independiente')}
                        className={`px-2 py-2 rounded-lg border-2 transition-all flex items-center justify-center gap-1.5 text-xs font-semibold ${
                          manualOrder.driver === 'independiente'
                            ? 'border-orange-500 bg-orange-500/20 text-orange-400'
                            : 'border-gray-700 bg-black/30 text-gray-400'
                        }`}
                        data-testid="driver-independiente"
                      >
                        <Truck className="h-3.5 w-3.5" /> Independiente
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Items list moved to left column (below catalog) for cleaner right side */}

              {/* Notes */}
              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-2">
                <Textarea
                  value={manualOrder.notes}
                  onChange={(e) => handleManualOrderChange('notes', e.target.value)}
                  placeholder="Notas / instrucciones especiales (opcional)"
                  className="bg-black/50 border-gray-700 text-white text-sm"
                  rows={1}
                />
              </div>

              {/* Referral code (walk-in) */}
              <div className="bg-gradient-to-r from-purple-900/20 to-pink-900/20 border border-purple-500/30 rounded-xl p-2">
                <div className="flex items-center gap-2">
                  <Gift className="h-3.5 w-3.5 text-purple-400 shrink-0" />
                  <span className="text-purple-300 text-xs font-semibold whitespace-nowrap">Cód. Recomendación:</span>
                  <Input
                    value={manualOrder.referral_code}
                    onChange={(e) => setManualOrder(prev => ({
                      ...prev,
                      referral_code: e.target.value.toUpperCase(),
                      referral_valid: null
                    }))}
                    onBlur={validateManualReferralCode}
                    placeholder="JOSE482 (opcional)"
                    className="bg-black/50 border-gray-700 text-white h-8 text-sm font-mono uppercase flex-1"
                    data-testid="manual-referral-code"
                  />
                  <Button
                    type="button"
                    size="sm"
                    onClick={validateManualReferralCode}
                    className="bg-purple-600 hover:bg-purple-700 h-8 text-xs px-3"
                    data-testid="btn-validate-manual-referral"
                  >
                    Validar
                  </Button>
                </div>
                {manualOrder.referral_valid && (
                  <div className="mt-1.5 text-xs">
                    {manualOrder.referral_valid.valid ? (
                      <span className="text-green-400" data-testid="referral-valid-msg">
                        ✅ Recomendado por <b>{manualOrder.referral_valid.referrer_name}</b>
                      </span>
                    ) : (
                      <span className="text-red-400" data-testid="referral-invalid-msg">
                        ❌ {manualOrder.referral_valid.reason === 'not_found' ? 'Código no encontrado' :
                             manualOrder.referral_valid.reason === 'blocked' ? 'Código bloqueado' :
                             'Código inválido'}
                      </span>
                    )}
                  </div>
                )}
              </div>

              <InvoiceToggle
                value={manualOrder.invoice || EMPTY_INVOICE}
                gross={manualTotal}
                onChange={handleManualInvoiceChange}
                idPrefix="manual"
              />

              {/* Total & Submit - compact */}
              <div className="bg-gradient-to-r from-orange-900/30 to-red-900/30 border border-orange-500/50 rounded-xl p-2">
                <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs">
                  <span className="text-gray-400">Subtotal: <span className="text-white font-semibold">${manualSubtotalGross.toFixed(2)}</span></span>
                  {manualPercentDiscount > 0 && (
                    <span className="text-purple-400">
                      Desc{isAllCourtesy ? ' (CORTESÍA)' : ''}: -${manualPercentDiscount.toFixed(2)}
                    </span>
                  )}
                  {manualOrder.delivery_type === 'domicilio' && (
                    <span className="text-gray-400">Envío: <span className="text-white">+${COSTO_ENVIO.toFixed(2)}</span></span>
                  )}
                  {manualVipDiscount > 0 && (
                    <span className="text-yellow-400">VIP: -${manualVipDiscount.toFixed(2)}</span>
                  )}
                </div>
                <div className="flex justify-between items-center text-lg font-bold pt-1 mt-1 border-t border-orange-500/30">
                  <span className="text-white">Total:</span>
                  <span className="text-orange-500" data-testid="manual-total">${manualChargeTotal.toFixed(2)} MXN</span>
                </div>
              </div>

              <div className={`grid ${manualOrder.delivery_type === 'domicilio' ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-3'} gap-2 mb-56 md:mb-40`}>
                <Button
                  type="button"
                  onClick={(e) => submitManualOrder(e, { chargeAndPrint: true })}
                  disabled={isSubmittingManual}
                  className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-sm h-10"
                  data-testid="btn-charge-print"
                >
                  <Printer className="h-4 w-4 mr-1" />
                  {isSubmittingManual ? '...' : 'Cobrar+Imprimir'}
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmittingManual}
                  className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-sm h-10"
                  data-testid="btn-create-order"
                >
                  {isSubmittingManual ? '...' : 'Crear Pedido'}
                </Button>
                <Button
                  type="button"
                  onClick={copyManualOrderForConfirmation}
                  variant="outline"
                  className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10 h-10 text-sm"
                  data-testid="btn-copy-manual-ticket"
                >
                  <Copy className="h-4 w-4 mr-1" />
                  Cliente
                </Button>
                {manualOrder.delivery_type === 'domicilio' && (
                  <Button
                    type="button"
                    onClick={copyManualOrderForDriver}
                    variant="outline"
                    className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10 h-10 text-sm"
                    data-testid="btn-copy-driver"
                  >
                    <Truck className="h-4 w-4 mr-1" />
                    Repartidor
                  </Button>
                )}
              </div>
            </form>
            </div>
          </div>
        )}

        {/* VIP Search Modal */}
        {showVipSearch && (
          <div className="fixed inset-0 bg-black/80 flex items-start justify-center z-50 p-4 overflow-y-auto" data-testid="vip-search-modal">
            <div className="bg-gray-900 border border-yellow-500/30 rounded-xl p-6 max-w-2xl w-full my-8">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-white font-bold text-lg flex items-center">
                  <Crown className="h-5 w-5 mr-2 text-yellow-500" />
                  Buscar Cliente VIP
                </h3>
                <Button variant="ghost" onClick={() => setShowVipSearch(false)} className="text-gray-400">
                  <XCircle className="h-5 w-5" />
                </Button>
              </div>
              
              <div className="space-y-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    autoFocus
                    placeholder="Buscar por nombre, teléfono o código VIP..."
                    value={vipSearchQuery}
                    onChange={(e) => {
                      setVipSearchQuery(e.target.value);
                      searchVipClients(e.target.value);
                    }}
                    className="pl-10 bg-black/50 border-gray-700 text-white"
                    data-testid="vip-search-input"
                  />
                </div>
                
                <div className="max-h-80 overflow-y-auto space-y-2">
                  {(vipSearchQuery.length >= 2 ? vipSearchResults : vipClients).map(client => (
                    <button
                      key={client.id}
                      onClick={() => selectVipForOrder(client)}
                      className="w-full flex justify-between items-center p-3 bg-black/30 hover:bg-yellow-900/20 rounded-lg transition-colors text-left border border-gray-800 hover:border-yellow-500/30"
                      data-testid={`vip-result-${client.code}`}
                    >
                      <div>
                        <p className="text-white font-semibold flex items-center gap-2">
                          <Star className="h-4 w-4 text-yellow-500" />
                          {client.name}
                        </p>
                        <p className="text-gray-400 text-sm">📱 {client.phone}</p>
                        <p className="text-gray-500 text-xs font-mono">Código: {client.code}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-green-400 font-bold">{client.points?.toFixed(2) || '0.00'} pts</p>
                        <p className="text-gray-500 text-xs">{client.visit_count || 0} visitas</p>
                      </div>
                    </button>
                  ))}
                  
                  {vipSearchQuery.length >= 2 && vipSearchResults.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <p>No se encontraron clientes</p>
                    </div>
                  )}
                  
                  {vipSearchQuery.length < 2 && vipClients.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <Crown className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>No hay clientes VIP registrados</p>
                    </div>
                  )}
                </div>
                
                <div className="pt-4 border-t border-gray-800">
                  <Button
                    onClick={() => {
                      setShowVipSearch(false);
                      setShowVipForm(true);
                      setActiveTab('vip');
                    }}
                    variant="outline"
                    className="w-full border-yellow-500/40 text-yellow-400"
                  >
                    <Plus className="h-4 w-4 mr-2" /> Registrar Nuevo Cliente VIP
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* VIP Tab */}
        {activeTab === 'vip' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-white flex items-center">
                <Crown className="mr-2 h-6 w-6 text-yellow-500" />
                Clientes VIP ({vipClients.length})
              </h2>
              <div className="flex gap-2">
                {vipClients.length > 0 && (
                  <Button
                    onClick={() => setShowVipBulkInvite(true)}
                    className="bg-pink-600 hover:bg-pink-700"
                    data-testid="btn-invite-all-vip"
                  >
                    <Send className="h-4 w-4 mr-2" /> Invitar a Todos
                  </Button>
                )}
                <Button
                  onClick={() => setShowVipForm(true)}
                  className="bg-yellow-600 hover:bg-yellow-700"
                  data-testid="btn-new-vip"
                >
                  <Plus className="h-4 w-4 mr-2" /> Nuevo VIP
                </Button>
              </div>
            </div>

            {/* Search bar */}
            {vipClients.length > 0 && (
              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Buscar cliente por nombre, teléfono o código VIP..."
                    value={vipTabSearch}
                    onChange={(e) => setVipTabSearch(e.target.value)}
                    className="pl-10 bg-black/50 border-gray-700 text-white"
                    data-testid="vip-tab-search-input"
                  />
                  {vipTabSearch && (
                    <button
                      onClick={() => setVipTabSearch('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* New/Edit VIP Form Modal */}
            {(showVipForm || editingVipClient) && (
              <div className="fixed inset-0 bg-black/80 flex items-start justify-center z-50 p-4 overflow-y-auto">
                <div className="bg-gray-900 border border-yellow-500/30 rounded-xl p-6 max-w-md w-full my-8">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-white font-bold text-lg flex items-center">
                      <Crown className="h-5 w-5 mr-2 text-yellow-500" />
                      {editingVipClient ? 'Editar Cliente VIP' : 'Registrar Cliente VIP'}
                    </h3>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setShowVipForm(false);
                        setEditingVipClient(null);
                        setNewVipClient({ name: '', phone: '', address: '', referral_code: '' });
                        setReferralCodePreview(null);
                      }}
                      className="text-gray-400"
                    >
                      <XCircle className="h-5 w-5" />
                    </Button>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <Label className="text-gray-300">Nombre completo *</Label>
                      <Input
                        value={editingVipClient ? editingVipClient.name : newVipClient.name}
                        onChange={(e) => editingVipClient
                          ? setEditingVipClient({ ...editingVipClient, name: e.target.value })
                          : setNewVipClient({ ...newVipClient, name: e.target.value })
                        }
                        placeholder="Juan Pérez"
                        className="mt-1 bg-black/50 border-gray-700 text-white"
                        data-testid="input-vip-name"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300">Teléfono *</Label>
                      <Input
                        value={editingVipClient ? editingVipClient.phone : newVipClient.phone}
                        onChange={(e) => editingVipClient
                          ? setEditingVipClient({ ...editingVipClient, phone: e.target.value })
                          : setNewVipClient({ ...newVipClient, phone: e.target.value })
                        }
                        placeholder="877 123 4567"
                        className="mt-1 bg-black/50 border-gray-700 text-white"
                        data-testid="input-vip-phone"
                      />
                      <p className="text-gray-500 text-xs mt-1">El código VIP serán los últimos 8 dígitos</p>
                    </div>
                    {editingVipClient && (
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-gray-300">RFC <span className="text-gray-500 text-xs">(facturación)</span></Label>
                          <Input
                            value={editingVipClient.rfc || ''}
                            maxLength={13}
                            onChange={(e) => setEditingVipClient({ ...editingVipClient, rfc: e.target.value.toUpperCase() })}
                            placeholder="XAXX010101000"
                            className="mt-1 bg-black/50 border-gray-700 text-white font-mono uppercase"
                            data-testid="input-vip-rfc"
                          />
                        </div>
                        <div>
                          <Label className="text-gray-300">Razón social</Label>
                          <Input
                            value={editingVipClient.razon_social || ''}
                            onChange={(e) => setEditingVipClient({ ...editingVipClient, razon_social: e.target.value })}
                            placeholder="Opcional"
                            className="mt-1 bg-black/50 border-gray-700 text-white"
                            data-testid="input-vip-razon-social"
                          />
                        </div>
                      </div>
                    )}
                    {/* Referral code input — only shown on CREATE (not edit) */}
                    {!editingVipClient && (
                      <div>
                        <Label className="text-gray-300 flex items-center gap-1">🎁 ¿Alguien te recomendó? <span className="text-gray-500 text-xs">(opcional)</span></Label>
                        <div className="flex gap-2 mt-1">
                          <Input
                            value={newVipClient.referral_code}
                            onChange={(e) => { setNewVipClient({ ...newVipClient, referral_code: e.target.value.toUpperCase() }); setReferralCodePreview(null); }}
                            placeholder="Ej: JOSE482"
                            className="flex-1 bg-black/50 border-gray-700 text-white uppercase font-mono"
                            data-testid="input-vip-referral-code"
                          />
                          <Button
                            type="button"
                            onClick={validateReferralCodeInput}
                            disabled={!newVipClient.referral_code}
                            className="bg-pink-600 hover:bg-pink-500 disabled:opacity-40"
                            data-testid="btn-validate-referral-code"
                          >
                            Validar
                          </Button>
                        </div>
                        {referralCodePreview?.valid && (
                          <p className="text-green-400 text-xs mt-1">✅ Recomendado por {referralCodePreview.referrer_name}</p>
                        )}
                        {referralCodePreview && !referralCodePreview.valid && (
                          <p className="text-red-400 text-xs mt-1">❌ {referralCodePreview.reason === 'not_found' ? 'Código no existe' : referralCodePreview.reason === 'blocked' ? 'Código bloqueado' : 'Código inválido'}</p>
                        )}
                      </div>
                    )}
                    <div>
                      <Label className="text-gray-300">Dirección</Label>
                      <Textarea
                        value={editingVipClient ? editingVipClient.address : newVipClient.address}
                        onChange={(e) => editingVipClient
                          ? setEditingVipClient({ ...editingVipClient, address: e.target.value })
                          : setNewVipClient({ ...newVipClient, address: e.target.value })
                        }
                        placeholder="Calle, número, colonia"
                        className="mt-1 bg-black/50 border-gray-700 text-white"
                        rows={2}
                        data-testid="input-vip-address"
                      />
                    </div>
                    
                    <div className="flex gap-3 pt-4">
                      <Button
                        onClick={editingVipClient ? updateVipClient : createVipClient}
                        className="flex-1 bg-yellow-600 hover:bg-yellow-700"
                        data-testid="btn-save-vip"
                      >
                        {editingVipClient ? 'Guardar' : 'Registrar VIP'}
                      </Button>
                      <Button
                        onClick={() => {
                          setShowVipForm(false);
                          setEditingVipClient(null);
                          setNewVipClient({ name: '', phone: '', address: '', referral_code: '' });
                          setReferralCodePreview(null);
                        }}
                        variant="outline"
                        className="flex-1"
                      >
                        Cancelar
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* VIP Clients List */}
            {vipClients.length === 0 ? (
              <div className="text-center py-12 bg-gray-900/50 border border-gray-800 rounded-xl">
                <Crown className="h-16 w-16 text-yellow-500/50 mx-auto mb-4" />
                <p className="text-gray-400 text-lg">No hay clientes VIP todavía</p>
                <p className="text-gray-500 text-sm">Registra el primero para empezar a acumular puntos</p>
              </div>
            ) : (() => {
              const q = vipTabSearch.trim().toLowerCase();
              const filteredVips = q
                ? vipClients.filter(c =>
                    c.name?.toLowerCase().includes(q) ||
                    c.phone?.toLowerCase().includes(q) ||
                    c.code?.toLowerCase().includes(q)
                  )
                : vipClients;
              
              if (filteredVips.length === 0) {
                return (
                  <div className="text-center py-8 bg-gray-900/30 border border-gray-800 rounded-xl">
                    <Search className="h-10 w-10 text-gray-600 mx-auto mb-2" />
                    <p className="text-gray-400">No se encontraron clientes con "{vipTabSearch}"</p>
                  </div>
                );
              }
              
              return (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredVips.map(client => (
                    <div
                      key={client.id}
                      className="bg-gradient-to-br from-yellow-900/20 to-amber-900/10 border border-yellow-500/30 rounded-xl p-4"
                      data-testid={`vip-card-${client.code}`}
                    >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-2">
                        <Crown className="h-5 w-5 text-yellow-500" />
                        <div>
                          <p className="text-white font-bold">{client.name}</p>
                          <p className="text-gray-400 text-sm">📱 {client.phone}</p>
                          {client.rfc && (
                            <p className="text-sky-300 text-xs font-mono" data-testid={`vip-rfc-${client.code}`}>🧾 RFC: {client.rfc}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => copyVipMarketing(client)}
                          className="text-pink-400 hover:bg-pink-900/20 p-1 h-8 w-8"
                          data-testid={`btn-marketing-vip-${client.code}`}
                          title="Copiar invitación de marketing"
                        >
                          <Send className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => adjustVipPoints(client)}
                          className="text-yellow-400 hover:bg-yellow-900/20 p-1 h-8 w-8"
                          data-testid={`btn-adjust-vip-${client.code}`}
                          title="Ajustar puntos manualmente"
                        >
                          <Gift className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditingVipClient(client)}
                          className="text-blue-400 hover:bg-blue-900/20 p-1 h-8 w-8"
                          data-testid={`btn-edit-vip-${client.code}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => deleteVipClient(client.id)}
                          className="text-red-400 hover:bg-red-900/20 p-1 h-8 w-8"
                          data-testid={`btn-delete-vip-${client.code}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    
                    <div className="bg-black/30 rounded-lg p-3 mb-3">
                      <p className="text-gray-400 text-xs">Código VIP</p>
                      <p className="text-yellow-400 font-mono text-lg font-bold">{client.code}</p>
                    </div>

                    {/* Referral code card */}
                    {client.referral_code && (
                      <div className="bg-gradient-to-br from-pink-900/20 to-fuchsia-900/10 border border-pink-500/30 rounded-lg p-3 mb-3">
                        <p className="text-pink-300 text-xs font-semibold mb-1 flex items-center gap-1">🎁 Código de Recomendación</p>
                        <p className="text-pink-200 font-mono text-lg font-bold tracking-wider mb-2">{client.referral_code}</p>
                        <div className="grid grid-cols-2 gap-2 mb-3">
                          <button
                            onClick={() => {
                              const shortLink = `${window.location.origin}/r/${client.referral_code}`;
                              const msg = `🍗 ¡Te invito a probar Mr Crunch 28! Usa mi código *${client.referral_code}* en tu primera compra y recibe un burrito GRATIS al realizar una compra mínima de $150. ¡Todo está delicioso! 😋\n\n📲 ${shortLink}\n📞 WhatsApp: 877 122 8589`;
                              navigator.clipboard.writeText(msg);
                              toast.success(`📋 Mensaje con link corto copiado`);
                            }}
                            className="bg-gray-700 hover:bg-gray-600 text-white text-xs font-bold py-2 px-2 rounded-md flex items-center justify-center gap-1 transition-colors"
                            data-testid={`btn-copy-ref-${client.code}`}
                            title="Copiar mensaje amistoso con el link corto"
                          >
                            📋 Copiar
                          </button>
                          <button
                            onClick={() => {
                              const shortLink = `${window.location.origin}/r/${client.referral_code}`;
                              const msg = `🍗 ¡Te invito a probar Mr Crunch 28! Usa mi código *${client.referral_code}* en tu primera compra y recibe un burrito GRATIS al realizar una compra mínima de $150. ¡Todo está delicioso! 😋\n\n📲 ${shortLink}\n📞 WhatsApp: 877 122 8589`;
                              const phone = String(client.phone || '').replace(/\D/g, '');
                              const target = phone.length >= 10 ? `52${phone.slice(-10)}` : '';
                              window.open(`https://wa.me/${target}?text=${encodeURIComponent(msg)}`, '_blank');
                            }}
                            className="bg-green-600 hover:bg-green-500 text-white text-xs font-bold py-2 px-2 rounded-md flex items-center justify-center gap-1 transition-colors"
                            data-testid={`btn-wa-ref-${client.code}`}
                            title="Invitar por WhatsApp con el link corto"
                          >
                            💬 WhatsApp
                          </button>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          <div>
                            <p className="text-gray-400">Referidos</p>
                            <p className="text-white font-bold">{client.referral_count || 0}</p>
                          </div>
                          <div>
                            <p className="text-gray-400">Saldo</p>
                            <p className="text-green-400 font-bold">{(client.referral_balance || 0).toFixed(2)}</p>
                          </div>
                          <div>
                            <p className="text-gray-400">Ganado</p>
                            <p className="text-yellow-400 font-bold">{(client.referral_lifetime || 0).toFixed(2)}</p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <div className="bg-black/30 rounded-lg p-2 text-center">
                        <p className="text-gray-500 text-xs">Puntos</p>
                        <p className="text-green-400 font-bold">{client.points?.toFixed(2) || '0.00'}</p>
                      </div>
                      <div className="bg-black/30 rounded-lg p-2 text-center">
                        <p className="text-gray-500 text-xs">Visitas</p>
                        <p className="text-white font-bold">{client.visit_count || 0}</p>
                      </div>
                    </div>
                    
                    <div className="bg-black/30 rounded-lg p-2 text-center">
                      <p className="text-gray-500 text-xs">Total comprado</p>
                      <p className="text-white font-bold">${client.total_purchases?.toFixed(2) || '0.00'}</p>
                    </div>
                    
                    {client.address && (
                      <p className="text-gray-500 text-xs mt-2 line-clamp-2">📍 {client.address}</p>
                    )}
                  </div>
                  ))}
                </div>
              );
            })()}

            {/* Bulk Invite VIP Modal */}
            {showVipBulkInvite && (
              <div className="fixed inset-0 bg-black/80 flex items-start justify-center z-50 p-4 overflow-y-auto" data-testid="vip-bulk-invite-modal">
                <div className="bg-gray-900 border border-pink-500/40 rounded-2xl p-6 max-w-3xl w-full my-8 shadow-2xl">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-white font-bold text-xl flex items-center">
                      <Send className="h-6 w-6 mr-2 text-pink-500" />
                      Invitar a Todos los VIP ({vipClients.length})
                    </h3>
                    <Button
                      variant="ghost"
                      onClick={() => setShowVipBulkInvite(false)}
                      className="text-gray-400"
                    >
                      <XCircle className="h-5 w-5" />
                    </Button>
                  </div>

                  <p className="text-gray-400 text-sm mb-4">
                    Envía a cada cliente VIP un mensaje personalizado según sus puntos.
                    Usa <span className="text-green-400 font-semibold">"WhatsApp"</span> para abrir el chat directamente,
                    o <span className="text-pink-400 font-semibold">"Copiar todos"</span> para enviarlos manualmente.
                  </p>

                  <div className="mb-4 flex flex-wrap gap-2">
                    <Button
                      onClick={copyAllVipMarketing}
                      className="bg-pink-600 hover:bg-pink-700"
                      data-testid="btn-copy-all-vip"
                    >
                      <Copy className="h-4 w-4 mr-2" /> Copiar todos los mensajes
                    </Button>
                  </div>

                  <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
                    {vipClients.map(client => {
                      const phoneDigits = normalizePhoneForWhatsApp(client.phone);
                      const msg = buildVipMarketingMessage(client);
                      const waUrl = phoneDigits
                        ? `https://wa.me/${phoneDigits}?text=${encodeURIComponent(msg)}`
                        : null;
                      const points = Number(client.points) || 0;
                      return (
                        <div
                          key={client.id}
                          className="bg-black/40 border border-gray-800 rounded-lg p-3 flex items-center justify-between gap-3"
                          data-testid={`vip-bulk-row-${client.code}`}
                        >
                          <div className="min-w-0 flex-1">
                            <p className="text-white font-semibold truncate">{client.name}</p>
                            <p className="text-gray-500 text-xs">
                              📱 {client.phone || '(sin teléfono)'} · <span className="text-green-400">{points.toFixed(0)} pts</span>
                            </p>
                          </div>
                          <div className="flex gap-1.5 shrink-0">
                            <Button
                              size="sm"
                              onClick={() => copyVipMarketing(client)}
                              className="bg-gray-700 hover:bg-gray-600 text-white h-8 px-2"
                              data-testid={`btn-bulk-copy-${client.code}`}
                              title="Copiar mensaje"
                            >
                              <Copy className="h-3.5 w-3.5" />
                            </Button>
                            {waUrl ? (
                              <a
                                href={waUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bg-green-600 hover:bg-green-700 text-white h-8 px-3 rounded-md flex items-center text-xs font-semibold gap-1"
                                data-testid={`btn-bulk-wa-${client.code}`}
                              >
                                <Send className="h-3.5 w-3.5" /> WhatsApp
                              </a>
                            ) : (
                              <span className="text-gray-600 text-xs italic px-2 self-center">sin tel</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="mt-4 flex justify-end">
                    <Button
                      onClick={() => setShowVipBulkInvite(false)}
                      className="bg-gray-700 hover:bg-gray-600"
                    >
                      Cerrar
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Orders Tab - Pedidos Pendientes */}
        {activeTab === 'orders' && (
          <div>
            <h2 className="text-xl font-bold text-white mb-4 flex items-center">
              <Package className="mr-2 h-6 w-6 text-orange-500" />
              Pedidos Pendientes de Cobro · Hoy ({pendingOrders.length})
            </h2>

            {/* Status Filter */}
            <div className="flex flex-wrap gap-2 mb-6">
              <Button 
                variant={statusFilter === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setStatusFilter('all')}
                className={statusFilter === 'all' ? 'bg-orange-500' : ''}
              >
                Todos ({pendingOrders.length})
              </Button>
              {Object.entries(STATUS_LABELS).map(([key, { label }]) => (
                <Button
                  key={key}
                  variant={statusFilter === key ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setStatusFilter(key)}
                  className={statusFilter === key ? 'bg-orange-500' : ''}
                >
                  {label} ({pendingOrders.filter(o => o.status === key).length})
                </Button>
              ))}
            </div>

            {pendingOrders.length === 0 ? (
              <div className="text-center py-12">
                <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                <p className="text-gray-400 text-lg">¡No hay pedidos pendientes!</p>
                <p className="text-gray-500">Todos los pedidos han sido cobrados</p>
              </div>
            ) : (
            
            /* Orders List */
            <div className="space-y-4">
              {filteredPendingOrders.map(order => {
                const statusInfo = STATUS_LABELS[order.status] || STATUS_LABELS.recibido;
                const StatusIcon = statusInfo.icon;
                const paymentInfo = PAYMENT_STATUS_LABELS[order.payment_status] || PAYMENT_STATUS_LABELS.pendiente;

                return (
                  <div key={order.id} className={`bg-gray-900/50 border rounded-xl p-4 ${order.payment_status === 'reembolsado' ? 'border-red-500/50 opacity-60' : 'border-gray-800'}`}>
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <p className="text-orange-400 font-mono">#{order.id.slice(0,8)}</p>
                        <p className="text-white font-semibold text-lg">{order.customer_name}</p>
                        <p className="text-gray-400 text-sm">{order.customer_phone}</p>
                      </div>
                      <div className="flex flex-col gap-1 items-end">
                        <div className={`${statusInfo.color} px-3 py-1 rounded-full flex items-center gap-1`}>
                          <StatusIcon className="h-4 w-4 text-white" />
                          <span className="text-white text-sm font-medium">{statusInfo.label}</span>
                        </div>
                        <div className={`${paymentInfo.color} px-2 py-0.5 rounded-full`}>
                          <span className="text-white text-xs font-medium">{paymentInfo.label}</span>
                        </div>
                      </div>
                    </div>

                    {order.delivery_type === 'domicilio' && order.customer_address && (
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(order.customer_address)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-indigo-900/30 border border-indigo-500/40 hover:border-indigo-400 rounded-lg p-2 mb-3 flex items-start gap-2 group transition-colors"
                        data-testid={`address-${order.id}`}
                      >
                        <MapPin className="h-5 w-5 text-indigo-400 shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <p className="text-indigo-300 text-xs font-bold uppercase tracking-wide">Dirección de entrega</p>
                          <p className="text-white font-medium leading-tight">{order.customer_address}</p>
                        </div>
                        <span className="text-indigo-400 text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
                          Abrir mapa →
                        </span>
                      </a>
                    )}

                    <div className="flex items-center gap-4 mb-3 text-sm">
                      <span className="text-gray-400">
                        {order.delivery_type === 'domicilio' ? '🚚 Domicilio' : '🏪 Ventanilla'}
                      </span>
                      <span className="text-gray-400">
                        {formatPayment(order.payment_method)}
                      </span>
                      <span className="text-white font-bold">${order.total}</span>
                    </div>

                    {/* Refund reason if exists */}
                    {order.refund_reason && (
                      <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-2 mb-3">
                        <p className="text-red-400 text-sm"><strong>Motivo reembolso:</strong> {order.refund_reason}</p>
                      </div>
                    )}

                    {/* Items */}
                    <div className="bg-black/30 rounded-lg p-3 mb-3 space-y-1">
                      {order.items?.map((item, idx) => (
                        <div key={`${order.id}-${item.id || idx}`}>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-300">{item.quantity}x {item.name}</span>
                            <span className="text-gray-400">${item.price * item.quantity}</span>
                          </div>
                          {item.notes && item.notes.trim() && (
                            <p className="text-yellow-400 text-xs italic ml-4 mt-0.5">↳ {item.notes}</p>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Status Actions */}
                    <div className="flex flex-wrap gap-2 mb-2">
                      {order.status === 'recibido' && (
                        <Button size="sm" onClick={() => updateOrderStatus(order.id, 'preparando')} className="bg-blue-600">
                          <ChefHat className="h-4 w-4 mr-1" /> Preparando
                        </Button>
                      )}
                      {/* Listo button available for both recibido & preparando so user can skip 'preparando' if desired */}
                      {(order.status === 'recibido' || order.status === 'preparando') && (
                        <Button size="sm" onClick={() => updateOrderStatus(order.id, 'listo')} className="bg-green-600" data-testid={`btn-mark-listo-${order.id}`}>
                          <CheckCircle className="h-4 w-4 mr-1" /> Listo
                        </Button>
                      )}
                      
                      {order.status === 'listo' && order.delivery_type === 'domicilio' && (
                        <Button size="sm" onClick={() => updateOrderStatus(order.id, 'en_camino')} className="bg-indigo-600">
                          <Truck className="h-4 w-4 mr-1" /> En Camino
                        </Button>
                      )}
                      
                      {order.status === 'listo' && order.delivery_type === 'ventanilla' && (
                        <Button size="sm" onClick={() => updateOrderStatus(order.id, 'entregado')} className="bg-green-600">
                          <CheckCircle className="h-4 w-4 mr-1" /> Entregado
                        </Button>
                      )}

                      {order.status === 'en_camino' && order.delivery_type === 'domicilio' && (
                        <Button size="sm" onClick={() => updateOrderStatus(order.id, 'entregado')} className="bg-green-600">
                          <CheckCircle className="h-4 w-4 mr-1" /> Marcar Entregado
                        </Button>
                      )}
                    </div>

                    {/* Payment & Print Actions */}
                    <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-700">
                      {/* Cobrar - only show if not paid and not refunded */}
                      {order.payment_status !== 'pagado' && order.payment_status !== 'reembolsado' && (
                        <Button 
                          size="sm" 
                          onClick={() => markAsPaid(order)} 
                          className="bg-green-700 hover:bg-green-600"
                        >
                          <CircleDollarSign className="h-4 w-4 mr-1" /> Cobrar
                        </Button>
                      )}
                      
                      {/* Editar */}
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => startEditingOrder(order)} 
                        className="border-blue-600 text-blue-400 hover:bg-blue-900/20"
                      >
                        <Edit className="h-4 w-4 mr-1" /> Editar
                      </Button>
                      
                      {/* Reimprimir */}
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => printTicket(order)} 
                        className="border-gray-600 text-gray-300 hover:bg-gray-800"
                      >
                        <Printer className="h-4 w-4 mr-1" /> Reimprimir
                      </Button>

                      {/* Copiar */}
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => copyOrderToClipboard(order)} 
                        className="border-gray-600 text-gray-300 hover:bg-gray-800"
                      >
                        <Copy className="h-4 w-4 mr-1" /> Copiar
                      </Button>

                      {/* Despejar/Cancelar */}
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => cancelOrder(order.id)} 
                        className="border-red-600 text-red-400 hover:bg-red-900/20"
                      >
                        <XCircle className="h-4 w-4 mr-1" /> Despejar
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
            )}

            {/* Edit Order Modal */}
            {editingOrder && (
              <div className="fixed inset-0 bg-black/80 flex items-start justify-center z-50 p-4 overflow-y-auto">
                <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 max-w-4xl w-full my-8">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-white font-bold text-lg">
                      Editar Pedido #{editingOrder.id?.slice(0,8)}
                    </h3>
                    <Button variant="ghost" onClick={() => setEditingOrder(null)} className="text-gray-400">
                      <XCircle className="h-5 w-5" />
                    </Button>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Left - Order Items */}
                    <div>
                      <h4 className="text-white font-semibold mb-3">Productos</h4>
                      <div className="space-y-2">
                        {editingOrder.items.map((item, index) => (
                          <div key={item.id || item._key || `eidx-${index}`} className="flex gap-2 items-center bg-black/30 p-2 rounded-lg">
                            <Input
                              value={item.name}
                              onChange={(e) => updateOrderItem(index, 'name', e.target.value)}
                              placeholder="Producto"
                              className="flex-1 bg-black/50 border-gray-700 text-white text-sm"
                            />
                            <Input
                              type="number"
                              value={item.quantity}
                              onChange={(e) => updateOrderItem(index, 'quantity', e.target.value)}
                              className="w-16 bg-black/50 border-gray-700 text-white text-sm"
                              min="1"
                            />
                            <Input
                              type="number"
                              value={item.price}
                              onChange={(e) => updateOrderItem(index, 'price', e.target.value)}
                              className="w-20 bg-black/50 border-gray-700 text-white text-sm"
                            />
                            <span className="text-gray-400 text-sm w-20 text-right">
                              ${(item.price * item.quantity).toFixed(2)}
                            </span>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => removeOrderItem(index)}
                              className="text-red-400 hover:bg-red-900/20 p-1"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                      <Button onClick={addOrderItem} variant="outline" size="sm" className="mt-2 border-gray-700">
                        <PlusCircle className="h-4 w-4 mr-1" /> Agregar línea
                      </Button>
                      
                      <div className="mt-4 p-3 bg-orange-900/20 border border-orange-500/30 rounded-lg">
                        <div className="flex justify-between text-gray-300">
                          <span>Subtotal:</span>
                          <span>${editingOrder.subtotal?.toFixed(2) || '0.00'}</span>
                        </div>
                        {editingOrder.delivery_type === 'domicilio' && (
                          <div className="flex justify-between text-gray-300">
                            <span>Envío:</span>
                            <span>$50.00</span>
                          </div>
                        )}
                        <div className="flex justify-between text-white font-bold text-lg mt-1">
                          <span>Total:</span>
                          <span>${editingOrder.total?.toFixed(2) || '0.00'}</span>
                        </div>
                      </div>
                      
                      <div className="mt-4">
                        <Label className="text-gray-300">Notas</Label>
                        <Textarea
                          value={editingOrder.notes || ''}
                          onChange={(e) => setEditingOrder({...editingOrder, notes: e.target.value})}
                          className="mt-1 bg-black/50 border-gray-700 text-white"
                          rows={2}
                        />
                      </div>
                      
                      <div className="flex gap-3 mt-4">
                        <Button onClick={saveOrderChanges} className="flex-1 bg-orange-600 hover:bg-orange-700">
                          Guardar Cambios
                        </Button>
                        <Button onClick={() => setEditingOrder(null)} variant="outline" className="flex-1">
                          Cancelar
                        </Button>
                      </div>
                    </div>
                    
                    {/* Right - Catalog to add items */}
                    <div className="border-l border-gray-700 pl-6">
                      <h4 className="text-white font-semibold mb-3">Agregar desde Catálogo</h4>
                      <div className="max-h-96 overflow-y-auto space-y-2">
                        {MENU_CATEGORIES.map(category => {
                          const items = catalog[category] || [];
                          if (items.length === 0) return null;
                          
                          return (
                            <div key={category}>
                              <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">{category}</p>
                              <div className="space-y-1">
                                {items.filter(item => item.available !== false).map(item => (
                                  <button
                                    key={item.id}
                                    onClick={() => addCatalogItemToOrder(item)}
                                    className="w-full flex justify-between items-center p-2 bg-black/30 hover:bg-orange-900/30 rounded-lg transition-colors text-left"
                                  >
                                    <span className="text-white text-sm">{item.name}</span>
                                    <span className="text-green-400 text-sm font-medium">${item.price?.toFixed(2)}</span>
                                  </button>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Cobrados Tab - Pedidos Ya Cobrados */}
        {activeTab === 'cobrados' && (
          <div>
            <h2 className="text-xl font-bold text-white mb-4 flex items-center">
              <CheckCircle className="mr-2 h-6 w-6 text-green-500" />
              Pedidos Cobrados · Hoy ({paidOrders.length})
            </h2>

            {paidOrders.length === 0 ? (
              <div className="text-center py-12">
                <DollarSign className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400 text-lg">No hay pedidos cobrados todavía</p>
              </div>
            ) : (
              (() => {
                const ventanillaOrders = paidOrders.filter(o => o.delivery_type !== 'domicilio');
                const domicilioOrders = paidOrders.filter(o => o.delivery_type === 'domicilio');

                const renderOrderCard = (order) => {
                  const statusInfo = STATUS_LABELS[order.status] || STATUS_LABELS.recibido;
                  const StatusIcon = statusInfo.icon;
                  return (
                    <div key={order.id} className="bg-gray-900/50 border border-green-500/30 rounded-xl p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <p className="text-orange-400 font-mono text-sm">#{order.id.slice(0,8)}</p>
                          <p className="text-white font-semibold">{order.customer_name}</p>
                          <p className="text-gray-400 text-sm">{order.customer_phone}</p>
                        </div>
                        <div className="flex flex-col gap-1 items-end">
                          <div className={`${statusInfo.color} px-2 py-0.5 rounded-full flex items-center gap-1`}>
                            <StatusIcon className="h-3 w-3 text-white" />
                            <span className="text-white text-xs font-medium">{statusInfo.label}</span>
                          </div>
                          <div className="bg-green-600 px-2 py-0.5 rounded-full">
                            <span className="text-white text-xs font-medium">✓ Pagado</span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 mb-3 text-xs flex-wrap">
                        <span className="text-gray-400">{formatPayment(order.payment_method)}</span>
                        <span className="text-gray-500">
                          {new Date(order.created_at).toLocaleTimeString('es-MX', {hour: '2-digit', minute: '2-digit'})}
                        </span>
                      </div>

                      {order.delivery_type === 'domicilio' && order.customer_address && (
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(order.customer_address)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-indigo-900/30 border border-indigo-500/40 hover:border-indigo-400 rounded-lg p-2 mb-3 flex items-start gap-2 group transition-colors"
                        >
                          <MapPin className="h-4 w-4 text-indigo-400 shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <p className="text-indigo-300 text-[10px] font-bold uppercase tracking-wide">Dirección</p>
                            <p className="text-white text-sm leading-tight">{order.customer_address}</p>
                          </div>
                          <span className="text-indigo-400 text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
                            Mapa →
                          </span>
                        </a>
                      )}

                      <div className="bg-black/30 rounded-lg p-3 mb-3 space-y-1">
                        {order.items?.map((item, idx) => (
                          <div key={`${order.id}-${item.id || idx}`}>
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-300">{item.quantity}x {item.name}</span>
                              <span className="text-gray-400">${item.price * item.quantity}</span>
                            </div>
                            {item.notes && item.notes.trim() && (
                              <p className="text-yellow-400 text-xs italic ml-4 mt-0.5">↳ {item.notes}</p>
                            )}
                          </div>
                        ))}
                      </div>

                      <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-700">
                        {/* Status advance — allow marking Listo directly (skipping Preparando) so kitchen board updates fast */}
                        {order.status === 'recibido' && (
                          <Button size="sm" onClick={() => updateOrderStatus(order.id, 'preparando')} className="bg-blue-600 hover:bg-blue-500" data-testid={`btn-cob-preparando-${order.id}`}>
                            <ChefHat className="h-4 w-4 mr-1" /> Preparando
                          </Button>
                        )}
                        {(order.status === 'recibido' || order.status === 'preparando') && (
                          <Button size="sm" onClick={() => updateOrderStatus(order.id, 'listo')} className="bg-green-600 hover:bg-green-500" data-testid={`btn-cob-listo-${order.id}`}>
                            <CheckCircle className="h-4 w-4 mr-1" /> Listo
                          </Button>
                        )}
                        {order.status === 'listo' && (
                          <Button size="sm" onClick={() => updateOrderStatus(order.id, 'entregado')} className="bg-emerald-700 hover:bg-emerald-600" data-testid={`btn-cob-entregado-${order.id}`}>
                            <CheckCircle className="h-4 w-4 mr-1" /> Entregado
                          </Button>
                        )}
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => printTicket(order)} 
                          className="border-gray-600 text-gray-300 hover:bg-gray-800"
                        >
                          <Printer className="h-4 w-4 mr-1" /> Reimprimir
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => refundOrder(order.id)} 
                          className="border-red-600 text-red-400 hover:bg-red-900/20"
                        >
                          <RotateCcw className="h-4 w-4 mr-1" /> Reembolsar
                        </Button>
                      </div>
                    </div>
                  );
                };
                
                return (
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Columna Ventanilla */}
                    <div data-testid="col-ventanilla">
                      <div className="bg-orange-900/20 border border-orange-500/30 rounded-xl p-3 mb-3 flex items-center justify-between sticky top-20 z-10 backdrop-blur-sm">
                        <h3 className="text-white font-bold flex items-center gap-2">
                          <Store className="h-5 w-5 text-orange-500" />
                          Ventanilla ({ventanillaOrders.length})
                        </h3>
                      </div>
                      {ventanillaOrders.length === 0 ? (
                        <div className="text-center py-8 text-gray-500 bg-gray-900/30 rounded-xl">
                          <Store className="h-8 w-8 mx-auto mb-2 opacity-40" />
                          <p className="text-sm">Sin pedidos de ventanilla</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {ventanillaOrders.map(renderOrderCard)}
                        </div>
                      )}
                    </div>

                    {/* Columna Domicilio */}
                    <div data-testid="col-domicilio">
                      <div className="bg-indigo-900/20 border border-indigo-500/30 rounded-xl p-3 mb-3 sticky top-20 z-10 backdrop-blur-sm">
                        <div className="flex items-center justify-between">
                          <h3 className="text-white font-bold flex items-center gap-2">
                            <Truck className="h-5 w-5 text-indigo-400" />
                            A Domicilio ({domicilioOrders.length})
                          </h3>
                        </div>
                      </div>
                      {domicilioOrders.length === 0 ? (
                        <div className="text-center py-8 text-gray-500 bg-gray-900/30 rounded-xl">
                          <Truck className="h-8 w-8 mx-auto mb-2 opacity-40" />
                          <p className="text-sm">Sin pedidos a domicilio</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {domicilioOrders.map(renderOrderCard)}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()
            )}

            {/* Refunded Orders Section */}
            {refundedOrders.length > 0 && (
              <div className="mt-8">
                <h3 className="text-lg font-bold text-red-400 mb-4 flex items-center">
                  <XCircle className="mr-2 h-5 w-5" />
                  Reembolsados ({refundedOrders.length})
                </h3>
                <div className="space-y-3">
                  {refundedOrders.map(order => (
                    <div key={order.id} className="bg-gray-900/30 border border-red-500/20 rounded-xl p-3 opacity-60">
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="text-orange-400 font-mono">#{order.id.slice(0,8)}</span>
                          <span className="text-gray-400 ml-2">{order.customer_name}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-red-400">${order.total}</span>
                          <span className="bg-red-600 px-2 py-0.5 rounded-full text-white text-xs">Reembolsado</span>
                        </div>
                      </div>
                      {order.refund_reason && (
                        <p className="text-red-400/70 text-sm mt-1">Motivo: {order.refund_reason}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Catalog Tab */}
        {activeTab === 'catalogo' && (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center">
              <ShoppingBag className="mr-2 h-6 w-6 text-orange-500" />
              Catálogo de Productos
            </h2>

            {/* Add New Item Form */}
            <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-4">
              <h3 className="text-white font-semibold mb-4 flex items-center">
                <PlusCircle className="h-5 w-5 mr-2 text-green-500" />
                Agregar Nuevo Artículo
              </h3>
              <div className="grid md:grid-cols-5 gap-4">
                <div>
                  <Label className="text-gray-300">Categoría</Label>
                  <select
                    value={newItem.category}
                    onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
                    className="w-full mt-1 bg-black/50 border border-gray-700 text-white rounded-md px-3 py-2"
                  >
                    {MENU_CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-gray-300">Nombre</Label>
                  <Input
                    value={newItem.name}
                    onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                    placeholder="Ej: Combo #1"
                    className="mt-1 bg-black/50 border-gray-700 text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Descripción</Label>
                  <Input
                    value={newItem.description}
                    onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                    placeholder="Opcional"
                    className="mt-1 bg-black/50 border-gray-700 text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Precio ($)</Label>
                  <Input
                    type="number"
                    value={newItem.price}
                    onChange={(e) => setNewItem({ ...newItem, price: e.target.value })}
                    placeholder="0.00"
                    className="mt-1 bg-black/50 border-gray-700 text-white"
                  />
                </div>
                <div className="flex items-end">
                  <Button onClick={createMenuItem} className="w-full bg-green-600 hover:bg-green-700">
                    <Plus className="h-4 w-4 mr-2" /> Agregar
                  </Button>
                </div>
              </div>
            </div>

            {/* Edit Item Modal */}
            {editingItem && (
              <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
                <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 max-w-md w-full">
                  <h3 className="text-white font-semibold mb-4">Editar Artículo</h3>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-gray-300">Categoría</Label>
                      <select
                        value={editingItem.category}
                        onChange={(e) => setEditingItem({ ...editingItem, category: e.target.value })}
                        className="w-full mt-1 bg-black/50 border border-gray-700 text-white rounded-md px-3 py-2"
                      >
                        {MENU_CATEGORIES.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label className="text-gray-300">Nombre</Label>
                      <Input
                        value={editingItem.name}
                        onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })}
                        className="mt-1 bg-black/50 border-gray-700 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300">Descripción</Label>
                      <Input
                        value={editingItem.description || ''}
                        onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })}
                        className="mt-1 bg-black/50 border-gray-700 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300">Precio ($)</Label>
                      <Input
                        type="number"
                        value={editingItem.price}
                        onChange={(e) => setEditingItem({ ...editingItem, price: e.target.value })}
                        className="mt-1 bg-black/50 border-gray-700 text-white"
                      />
                    </div>
                    <div className="flex gap-3 pt-4">
                      <Button onClick={updateMenuItem} className="flex-1 bg-orange-600 hover:bg-orange-700">
                        Guardar
                      </Button>
                      <Button onClick={() => setEditingItem(null)} variant="outline" className="flex-1">
                        Cancelar
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Search bar */}
            <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-4">
              <div className="flex gap-3 items-center">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Buscar producto por nombre o descripción..."
                    value={catalogSearch}
                    onChange={(e) => setCatalogSearch(e.target.value)}
                    className="pl-10 bg-black/50 border-gray-700 text-white"
                    data-testid="catalog-search-input"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setReorderMode(!reorderMode)}
                  className={`px-4 py-2 rounded-md border-2 font-semibold text-sm whitespace-nowrap transition-all flex items-center gap-2 ${
                    reorderMode
                      ? 'border-orange-500 bg-orange-500/20 text-orange-300 shadow-lg shadow-orange-500/20'
                      : 'border-gray-700 bg-gray-900/40 text-gray-300 hover:border-gray-500 hover:bg-gray-800/60'
                  }`}
                  data-testid="catalog-reorder-toggle"
                  title={reorderMode ? 'Salir del modo reordenar' : 'Activar modo reordenar'}
                >
                  {reorderMode ? (
                    <>
                      <ChevronUp className="h-4 w-4" />
                      <ChevronDown className="h-4 w-4 -ml-2" />
                      Reordenando...
                    </>
                  ) : (
                    <>
                      <ChevronUp className="h-4 w-4" />
                      <ChevronDown className="h-4 w-4 -ml-2" />
                      Reordenar Productos
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Big Category Buttons Grid */}
            {!catalogSearch.trim() && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {['Todos', ...MENU_CATEGORIES].map(cat => {
                  const count = cat === 'Todos'
                    ? Object.values(catalog).reduce((sum, items) => sum + (items?.length || 0), 0)
                    : (catalog[cat]?.length || 0);
                  const active = catalogFilter === cat;
                  return (
                    <button
                      key={cat}
                      onClick={() => setCatalogFilter(cat)}
                      className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center justify-center min-h-[90px] ${
                        active
                          ? 'border-orange-500 bg-gradient-to-br from-orange-500/30 to-red-600/20 text-white shadow-lg shadow-orange-500/20 scale-[1.02]'
                          : 'border-gray-700 bg-gray-900/40 text-gray-300 hover:border-gray-500 hover:bg-gray-800/60'
                      }`}
                      data-testid={`catalog-filter-${cat}`}
                    >
                      <span className={`font-bold text-base ${active ? 'text-white' : 'text-gray-200'}`}>{cat}</span>
                      <span className={`text-xs mt-1 ${active ? 'text-orange-200' : 'text-gray-500'}`}>
                        {count} {count === 1 ? 'producto' : 'productos'}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Items grid - shows only selected category items (or search results) */}
            <div>
              {(() => {
                // Build flat list of items based on filter + search
                const collectItems = () => {
                  const result = [];
                  MENU_CATEGORIES.forEach(category => {
                    if (catalogFilter !== 'Todos' && catalogFilter !== category && !catalogSearch.trim()) return;
                    const allItems = catalog[category] || [];
                    const filtered = catalogSearch.trim()
                      ? allItems.filter(it =>
                          it.name?.toLowerCase().includes(catalogSearch.toLowerCase()) ||
                          it.description?.toLowerCase().includes(catalogSearch.toLowerCase())
                        )
                      : allItems;
                    filtered.forEach(item => result.push({ ...item, _category: category }));
                  });
                  return result;
                };
                
                const items = collectItems();
                
                if (items.length === 0) {
                  return (
                    <div className="text-center py-12 bg-gray-900/30 border border-gray-800 rounded-xl">
                      <ShoppingBag className="h-12 w-12 text-gray-600 mx-auto mb-3" />
                      <p className="text-gray-400">
                        {catalogSearch.trim() ? 'No se encontraron productos' : 'No hay productos en esta categoría'}
                      </p>
                    </div>
                  );
                }
                
                return (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {items.map(item => {
                      const categoryItems = catalog[item._category] || [];
                      const positionInCategory = categoryItems.findIndex(i => i.id === item.id);
                      const isFirst = positionInCategory <= 0;
                      const isLast = positionInCategory >= categoryItems.length - 1;
                      const canReorder = reorderMode && !catalogSearch.trim() && (catalogFilter === 'Todos' || catalogFilter === item._category);
                      return (
                      <div
                        key={item.id}
                        className={`bg-gray-900/60 border-2 rounded-xl p-4 transition-all ${
                          item.available
                            ? 'border-gray-700 hover:border-orange-500/40'
                            : 'border-red-900/40 opacity-60'
                        }`}
                        data-testid={`catalog-product-${item.id}`}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <span className="bg-gray-800 text-gray-400 text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full">
                            {item._category}
                          </span>
                          <div className="flex items-center gap-1">
                            {canReorder && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => moveCatalogItem(item._category, item.id, 'up')}
                                  disabled={isFirst}
                                  className={`p-1 rounded ${isFirst ? 'text-gray-700 cursor-not-allowed' : 'text-gray-400 hover:text-white hover:bg-gray-800'}`}
                                  title="Mover arriba"
                                  data-testid={`reorder-up-${item.id}`}
                                >
                                  <ChevronUp className="h-4 w-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => moveCatalogItem(item._category, item.id, 'down')}
                                  disabled={isLast}
                                  className={`p-1 rounded ${isLast ? 'text-gray-700 cursor-not-allowed' : 'text-gray-400 hover:text-white hover:bg-gray-800'}`}
                                  title="Mover abajo"
                                  data-testid={`reorder-down-${item.id}`}
                                >
                                  <ChevronDown className="h-4 w-4" />
                                </button>
                              </>
                            )}
                            {!item.available && (
                              <span className="bg-red-600 text-white text-xs px-2 py-0.5 rounded">
                                Agotado
                              </span>
                            )}
                          </div>
                        </div>
                        <h4 className="text-white font-bold text-lg leading-tight mb-1">{item.name}</h4>
                        {item.description && (
                          <p className="text-gray-500 text-xs mb-2 line-clamp-2">{item.description}</p>
                        )}
                        <p className="text-green-400 font-bold text-2xl mb-3">${item.price?.toFixed(2)}</p>
                        <div className="flex gap-2 pt-2 border-t border-gray-800">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => toggleItemAvailability(item.id)}
                            className={`flex-1 ${item.available ? 'text-green-500 hover:bg-green-900/20' : 'text-red-500 hover:bg-red-900/20'}`}
                            title={item.available ? 'Marcar agotado' : 'Marcar disponible'}
                          >
                            <Power className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setEditingItem(item)}
                            className="flex-1 text-blue-400 hover:bg-blue-900/20"
                            title="Editar"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => deleteMenuItem(item.id)}
                            className="flex-1 text-red-400 hover:bg-red-900/20"
                            title="Eliminar"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* Historical Receipts Tab */}
        {activeTab === 'referrals' && (
          <div className="space-y-4" data-testid="referrals-tab">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-xl font-bold text-white flex items-center">
                <Gift className="mr-2 h-6 w-6 text-pink-500" />
                Programa de Recomendaciones
              </h2>
              <div className="flex items-center gap-2">
                <span className={`px-3 py-1 rounded-full text-xs font-bold ${referralConfig.active ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300'}`}>
                  {referralConfig.active ? '✅ Activo' : '⏸️ Pausado'} · {referralConfig.percent}%
                </span>
                <Button size="sm" onClick={updateReferralConfig} className="bg-blue-600 hover:bg-blue-500" data-testid="btn-cfg-percent">
                  Cambiar %
                </Button>
                <Button size="sm" onClick={toggleReferralActive} className="bg-gray-700 hover:bg-gray-600" data-testid="btn-cfg-toggle">
                  {referralConfig.active ? 'Pausar' : 'Activar'}
                </Button>
                <Button size="sm" onClick={settlePendingReferralRewards} className="bg-amber-600 hover:bg-amber-500" data-testid="btn-settle-pending">
                  🎯 Acreditar pendientes
                </Button>
                <Button size="sm" onClick={recomputeReferralCounts} className="bg-purple-700 hover:bg-purple-600" data-testid="btn-recompute-counts">
                  🔄 Recalcular contadores
                </Button>
                <Button size="sm" onClick={() => window.open(`${BACKEND_URL}/api/admin/referrals/export.csv`, '_blank')} className="bg-emerald-700 hover:bg-emerald-600" data-testid="btn-export-csv">
                  📥 Exportar CSV
                </Button>
              </div>
            </div>

            {/* Stats cards */}
            {referralsStats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { label: 'Recomendadores', val: referralsStats.total_referrers, color: 'pink' },
                  { label: 'Referidos', val: referralsStats.total_referees, color: 'purple' },
                  { label: 'Puntos entregados', val: referralsStats.total_points_credited?.toFixed(2), color: 'green' },
                  { label: 'Pedidos generados', val: referralsStats.orders_generated, color: 'orange' },
                  { label: 'Pendientes', val: referralsStats.pending_count, color: 'yellow' },
                  { label: 'Acreditadas', val: referralsStats.credited_count, color: 'green' },
                  { label: 'Canceladas', val: referralsStats.cancelled_count, color: 'gray' },
                  { label: 'Revertidas', val: referralsStats.reversed_count, color: 'red' },
                ].map((s, i) => (
                  <div key={i} className={`bg-${s.color}-950/30 border border-${s.color}-500/30 rounded-xl p-3`}>
                    <p className="text-gray-400 text-xs">{s.label}</p>
                    <p className={`text-${s.color}-300 text-2xl font-black`}>{s.val ?? 0}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Top referrers */}
            {referralsStats?.top_referrers?.length > 0 && (
              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-4">
                <h3 className="text-white font-bold mb-2">🏆 Top Recomendadores</h3>
                <div className="grid gap-1.5">
                  {referralsStats.top_referrers.map((t, i) => (
                    <div key={t.id} className="flex items-center justify-between bg-black/30 rounded-lg px-3 py-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-yellow-400 font-black w-6 text-center">{i + 1}</span>
                        <span className="text-white font-semibold truncate">{t.name}</span>
                        <span className="text-pink-300 font-mono text-xs">{t.referral_code}</span>
                      </div>
                      <div className="flex gap-4 text-xs shrink-0">
                        <span className="text-gray-400">Ref: <span className="text-white font-bold">{t.referral_count}</span></span>
                        <span className="text-green-400 font-bold">{t.referral_lifetime.toFixed(2)} pts</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Filters */}
            <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-3 grid grid-cols-2 md:grid-cols-6 gap-2">
              <Input placeholder="Recomendador" value={referralFilters.referrer_name} onChange={(e) => setReferralFilters({...referralFilters, referrer_name: e.target.value})} className="bg-black/50 border-gray-700 text-white h-9 text-sm" />
              <Input placeholder="Teléfono" value={referralFilters.phone} onChange={(e) => setReferralFilters({...referralFilters, phone: e.target.value})} className="bg-black/50 border-gray-700 text-white h-9 text-sm" />
              <Input placeholder="Código" value={referralFilters.code} onChange={(e) => setReferralFilters({...referralFilters, code: e.target.value.toUpperCase()})} className="bg-black/50 border-gray-700 text-white h-9 text-sm" />
              <select value={referralFilters.status} onChange={(e) => setReferralFilters({...referralFilters, status: e.target.value})} className="bg-black/50 border border-gray-700 text-white rounded-md px-2 h-9 text-sm">
                <option value="">Todos los estados</option>
                <option value="acreditado">Acreditado</option>
                <option value="pendiente">Pendiente</option>
                <option value="cancelado">Cancelado</option>
                <option value="revertido">Revertido</option>
              </select>
              <Input type="date" value={referralFilters.date_from} onChange={(e) => setReferralFilters({...referralFilters, date_from: e.target.value})} className="bg-black/50 border-gray-700 text-white h-9 text-sm" />
              <div className="flex gap-1">
                <Button size="sm" onClick={() => fetchReferralsList()} className="bg-orange-600 hover:bg-orange-500 flex-1" data-testid="btn-refs-filter">🔍 Filtrar</Button>
                <Button size="sm" onClick={() => { setReferralFilters({ referrer_name: '', phone: '', code: '', status: '', date_from: '', date_to: '' }); fetchReferralsList({}); }} variant="ghost" className="text-gray-400">✕</Button>
              </div>
            </div>

            {/* Movements list */}
            <div className="bg-gray-900/50 border border-gray-800 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-black/50 text-gray-400 text-xs uppercase">
                    <tr>
                      <th className="px-3 py-2 text-left">Fecha</th>
                      <th className="px-3 py-2 text-left">Recomendador</th>
                      <th className="px-3 py-2 text-left">Referido</th>
                      <th className="px-3 py-2 text-left">Pedido</th>
                      <th className="px-3 py-2 text-right">Subtotal</th>
                      <th className="px-3 py-2 text-right">Puntos</th>
                      <th className="px-3 py-2 text-center">Tipo</th>
                      <th className="px-3 py-2 text-center">Estado</th>
                      <th className="px-3 py-2 text-center">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {referralsList.length === 0 && (
                      <tr><td colSpan={9} className="text-center py-8 text-gray-500">Sin movimientos</td></tr>
                    )}
                    {referralsList.map(m => (
                      <tr key={m.id} className="border-t border-gray-800 hover:bg-black/30" data-testid={`ref-mov-row-${m.id}`}>
                        <td className="px-3 py-2 text-gray-400 text-xs">{(m.created_at || '').slice(0, 16).replace('T', ' ')}</td>
                        <td className="px-3 py-2 text-white">{m.referrer_name || '-'}<br/><span className="text-pink-300 text-xs font-mono">{m.referrer_code}</span></td>
                        <td className="px-3 py-2 text-white">{m.referee_name || '-'}</td>
                        <td className="px-3 py-2 text-gray-300 font-mono text-xs">{m.order_number || '-'}</td>
                        <td className="px-3 py-2 text-right text-gray-300">${(m.eligible_subtotal || 0).toFixed(2)}</td>
                        <td className={`px-3 py-2 text-right font-bold ${m.points >= 0 ? 'text-green-400' : 'text-red-400'}`}>{(m.points || 0).toFixed(2)}</td>
                        <td className="px-3 py-2 text-center text-xs uppercase">{m.kind}</td>
                        <td className={`px-3 py-2 text-center text-xs font-bold ${
                          m.status === 'acreditado' ? 'text-green-400' :
                          m.status === 'pendiente' ? 'text-yellow-400' :
                          m.status === 'cancelado' ? 'text-gray-400' : 'text-red-400'}`}>{m.status}</td>
                        <td className="px-3 py-2 text-center">
                          {m.status === 'acreditado' && m.kind === 'abono' && (
                            <button onClick={() => cancelReferralMovement(m.id)} className="text-red-400 hover:text-red-300 text-xs" data-testid={`btn-cancel-mov-${m.id}`}>Cancelar</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'inventario' && <InventoryTab onChanged={fetchInventoryStatus} />}

        {activeTab === 'facturacion' && (
          <InvoicesTab backendUrl={BACKEND_URL} onPrint={(o) => printTicket(o, false)} />
        )}

        {activeTab === 'recibos' && (
          <div className="space-y-6" data-testid="recibos-tab">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-xl font-bold text-white flex items-center">
                <Receipt className="mr-2 h-6 w-6 text-orange-500" />
                Recibos Cobrados — Histórico
              </h2>
              <div className="flex items-center gap-2 bg-gray-900/60 border border-gray-700 rounded-xl px-3 py-2">
                <Label className="text-gray-400 text-xs uppercase tracking-wide">Fecha</Label>
                <Input
                  type="date"
                  value={receiptsDate}
                  onChange={(e) => {
                    setReceiptsDate(e.target.value);
                    fetchReceipts(e.target.value);
                  }}
                  className="bg-black/50 border-gray-700 text-white h-9 w-44"
                  data-testid="receipts-date-picker"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => fetchReceipts()}
                  className="border-gray-700 text-gray-300 hover:bg-gray-800 h-9"
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Summary cards */}
            {receiptsData.summary && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className="bg-gradient-to-br from-orange-900/30 to-red-950/30 border border-orange-500/30 rounded-xl p-3">
                  <p className="text-orange-300 text-xs">Total cobrado</p>
                  <p className="text-white text-2xl font-bold" data-testid="receipts-total">
                    ${receiptsData.summary.total_amount.toFixed(2)}
                  </p>
                  <p className="text-gray-500 text-xs">{receiptsData.summary.total_count} recibos</p>
                </div>
                <div className="bg-gradient-to-br from-green-900/30 to-emerald-950/30 border border-green-500/30 rounded-xl p-3">
                  <p className="text-green-300 text-xs">Efectivo</p>
                  <p className="text-white text-xl font-bold">${receiptsData.summary.cash_total.toFixed(2)}</p>
                </div>
                <div className="bg-gradient-to-br from-cyan-900/30 to-cyan-950/30 border border-cyan-500/30 rounded-xl p-3">
                  <p className="text-cyan-300 text-xs">Transferencia</p>
                  <p className="text-white text-xl font-bold">${receiptsData.summary.transfer_total.toFixed(2)}</p>
                </div>
                <div className="bg-gradient-to-br from-purple-900/30 to-purple-950/30 border border-purple-500/30 rounded-xl p-3">
                  <p className="text-purple-300 text-xs">Tarjeta</p>
                  <p className="text-white text-xl font-bold">${receiptsData.summary.card_total.toFixed(2)}</p>
                </div>
                <div className="bg-gradient-to-br from-red-900/20 to-red-950/30 border border-red-500/20 rounded-xl p-3">
                  <p className="text-red-300 text-xs">Reembolsos</p>
                  <p className="text-white text-xl font-bold">${receiptsData.summary.refunded_amount.toFixed(2)}</p>
                  <p className="text-gray-500 text-xs">{receiptsData.summary.refunded_count} pedidos</p>
                </div>
              </div>
            )}

            {/* List */}
            {receiptsLoading ? (
              <div className="text-center py-12 text-gray-400">Cargando...</div>
            ) : receiptsData.receipts.length === 0 ? (
              <div className="text-center py-16 bg-gray-900/30 rounded-xl">
                <Receipt className="h-12 w-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400">No hay recibos en esta fecha</p>
                <p className="text-gray-600 text-sm mt-1">Selecciona otra fecha del calendario</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                {receiptsData.receipts.map(receipt => {
                  const isRefunded = receipt.payment_status === 'reembolsado';
                  const ts = receipt.updated_at || receipt.created_at;
                  return (
                    <div
                      key={receipt.id}
                      className={`bg-gray-900/50 border rounded-xl p-3 ${
                        isRefunded ? 'border-red-500/30 opacity-80' : 'border-gray-700 hover:border-orange-500/40'
                      } transition-colors`}
                      data-testid={`receipt-${receipt.id}`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="text-orange-400 font-mono text-xs">#{receipt.id.slice(0,8)}</p>
                          <p className="text-white font-semibold text-sm">{receipt.customer_name || 'Cliente'}</p>
                          <p className="text-gray-500 text-xs">
                            {new Date(ts).toLocaleString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                            {' · '}
                            {receipt.delivery_type === 'domicilio' ? '🛵 Domicilio' : '🪟 Ventanilla'}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className={`font-bold text-lg ${isRefunded ? 'text-red-400 line-through' : 'text-green-400'}`}>
                            ${(receipt.total - (receipt.delivery_fee || 0)).toFixed(2)}
                          </p>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                            receipt.payment_method === 'efectivo' ? 'bg-green-700 text-green-100' :
                            receipt.payment_method === 'transferencia' ? 'bg-cyan-700 text-cyan-100' :
                            'bg-purple-700 text-purple-100'
                          }`}>
                            {receipt.payment_method?.toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div className="text-xs text-gray-400 mb-2 line-clamp-2">
                        {receipt.items?.map(i => `${i.quantity}× ${i.name}`).join(', ')}
                      </div>
                      {isRefunded && (
                        <div className="text-red-400 text-xs mb-2 flex items-center gap-1">
                          <RotateCcw className="h-3 w-3" /> Reembolsado{receipt.refund_reason ? ` · ${receipt.refund_reason}` : ''}
                        </div>
                      )}
                      <div className="flex gap-2 pt-2 border-t border-gray-800">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => printTicket(receipt, false)}
                          className="flex-1 text-blue-400 hover:bg-blue-900/20 h-8 text-xs"
                          data-testid={`reprint-${receipt.id}`}
                        >
                          <Printer className="h-3 w-3 mr-1" /> Reimprimir
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Cash Register Tab */}
        {activeTab === 'caja' && (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center">
              <Wallet className="mr-2 h-6 w-6 text-green-500" />
              Control de Caja
            </h2>

            {/* Cash register not opened */}
            {!cashRegister && (
              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
                <h3 className="text-white font-semibold mb-4">Abrir Caja del Día</h3>
                <p className="text-gray-400 mb-4">Ingresa el fondo inicial para comenzar el día.</p>
                <div className="flex gap-4 items-end">
                  <div className="flex-1 max-w-xs">
                    <Label className="text-gray-300">Fondo Inicial ($)</Label>
                    <Input
                      type="number"
                      value={openingBalance}
                      onChange={(e) => setOpeningBalance(e.target.value)}
                      placeholder="Ej: 500"
                      className="mt-1 bg-black/50 border-gray-700 text-white"
                    />
                  </div>
                  <Button onClick={openCashRegister} className="bg-green-600 hover:bg-green-700">
                    <Lock className="h-4 w-4 mr-2" /> Abrir Caja
                  </Button>
                </div>
              </div>
            )}

            {/* Cash register is closed for today */}
            {cashRegister && cashRegister.status === 'closed' && (
              <div className="bg-gradient-to-r from-green-900/20 to-emerald-900/20 border border-green-500/30 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <CheckCircle className="h-8 w-8 text-green-500" />
                  <div>
                    <h3 className="text-white font-semibold text-lg">Caja Cerrada</h3>
                    <p className="text-gray-400">El corte de caja de hoy ya fue realizado</p>
                  </div>
                </div>
                <div className="grid md:grid-cols-5 gap-4 mt-4">
                  <div className="text-center">
                    <p className="text-gray-400 text-sm">Fondo Inicial</p>
                    <p className="text-white text-xl font-bold">${cashRegister.opening_balance?.toFixed(2)}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-gray-400 text-sm">Efectivo</p>
                    <p className="text-white text-xl font-bold">${todayCashSales.toFixed(2)}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-gray-400 text-sm">Transferencia</p>
                    <p className="text-white text-xl font-bold">${todayTransferSales.toFixed(2)}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-gray-400 text-sm">Tarjeta</p>
                    <p className="text-white text-xl font-bold">${todayCardSales.toFixed(2)}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-gray-400 text-sm">Pedidos Hoy</p>
                    <p className="text-white text-xl font-bold">{paidOrdersForCaja.length}</p>
                  </div>
                </div>
                <p className="text-gray-500 text-sm mt-4">La caja se puede abrir nuevamente mañana.</p>
              </div>
            )}

            {/* Cash register is open */}
            {cashRegister && cashRegister.status === 'open' && (
              <>
                {/* Current status */}
                <div className="grid md:grid-cols-5 gap-4">
                  <div className="bg-gradient-to-br from-green-900/30 to-green-950/30 border border-green-500/30 rounded-xl p-4 text-center">
                    <Wallet className="h-8 w-8 text-green-500 mx-auto mb-2" />
                    <p className="text-gray-400 text-sm">Fondo Inicial</p>
                    <p className="text-white text-2xl font-bold">${cashRegister.opening_balance?.toFixed(2)}</p>
                  </div>
                  <div className="bg-gradient-to-br from-blue-900/30 to-blue-950/30 border border-blue-500/30 rounded-xl p-4 text-center">
                    <Banknote className="h-8 w-8 text-blue-500 mx-auto mb-2" />
                    <p className="text-gray-400 text-sm">Efectivo</p>
                    <p className="text-white text-2xl font-bold">${todayCashSales.toFixed(2)}</p>
                  </div>
                  <div className="bg-gradient-to-br from-cyan-900/30 to-cyan-950/30 border border-cyan-500/30 rounded-xl p-4 text-center">
                    <Smartphone className="h-8 w-8 text-cyan-500 mx-auto mb-2" />
                    <p className="text-gray-400 text-sm">Transferencia</p>
                    <p className="text-white text-2xl font-bold">${todayTransferSales.toFixed(2)}</p>
                  </div>
                  <div className="bg-gradient-to-br from-purple-900/30 to-purple-950/30 border border-purple-500/30 rounded-xl p-4 text-center">
                    <CreditCard className="h-8 w-8 text-purple-500 mx-auto mb-2" />
                    <p className="text-gray-400 text-sm">Tarjeta</p>
                    <p className="text-white text-2xl font-bold">${todayCardSales.toFixed(2)}</p>
                  </div>
                  <div className="bg-gradient-to-br from-orange-900/30 to-orange-950/30 border border-orange-500/30 rounded-xl p-4 text-center">
                    <Package className="h-8 w-8 text-orange-500 mx-auto mb-2" />
                    <p className="text-gray-400 text-sm">Pedidos Hoy</p>
                    <p className="text-white text-2xl font-bold">{paidOrdersForCaja.length}</p>
                  </div>
                </div>

                {/* Driver fees info banner */}
                {todayDeliveryFees > 0 && (
                  <div className="bg-yellow-900/10 border border-yellow-500/20 rounded-xl p-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Truck className="h-5 w-5 text-yellow-500" />
                      <div>
                        <p className="text-yellow-400 font-semibold text-sm">Envíos cobrados (Repartidor)</p>
                        <p className="text-gray-500 text-xs">No se acumula en caja — pertenece al repartidor</p>
                      </div>
                    </div>
                    <p className="text-yellow-400 font-bold text-lg">${todayDeliveryFees.toFixed(2)}</p>
                  </div>
                )}

                {/* Withdrawal form */}
                <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
                  <h3 className="text-white font-semibold mb-4 flex items-center">
                    <ArrowUpCircle className="h-5 w-5 mr-2 text-red-500" />
                    Registrar Retiro de Efectivo
                  </h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-gray-300">Monto ($)</Label>
                      <Input
                        type="number"
                        value={withdrawalAmount}
                        onChange={(e) => setWithdrawalAmount(e.target.value)}
                        placeholder="0.00"
                        className="mt-1 bg-black/50 border-gray-700 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300">Motivo</Label>
                      <Input
                        value={withdrawalReason}
                        onChange={(e) => setWithdrawalReason(e.target.value)}
                        placeholder="Ej: Pago a proveedor"
                        className="mt-1 bg-black/50 border-gray-700 text-white"
                      />
                    </div>
                    <div className="flex items-end">
                      <Button onClick={makeWithdrawal} className="bg-red-600 hover:bg-red-700 w-full">
                        <ArrowUpCircle className="h-4 w-4 mr-2" /> Registrar Retiro
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Deposit form */}
                <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
                  <h3 className="text-white font-semibold mb-4 flex items-center">
                    <ArrowDownCircle className="h-5 w-5 mr-2 text-green-500" />
                    Registrar Ingreso de Efectivo
                  </h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-gray-300">Monto ($)</Label>
                      <Input
                        type="number"
                        value={depositAmount}
                        onChange={(e) => setDepositAmount(e.target.value)}
                        placeholder="0.00"
                        className="mt-1 bg-black/50 border-gray-700 text-white"
                        data-testid="deposit-amount"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300">Motivo</Label>
                      <Input
                        value={depositReason}
                        onChange={(e) => setDepositReason(e.target.value)}
                        placeholder="Ej: Recarga de cambio"
                        className="mt-1 bg-black/50 border-gray-700 text-white"
                        data-testid="deposit-reason"
                      />
                    </div>
                    <div className="flex items-end">
                      <Button
                        onClick={makeDeposit}
                        className="bg-green-600 hover:bg-green-700 w-full"
                        data-testid="btn-deposit"
                      >
                        <ArrowDownCircle className="h-4 w-4 mr-2" /> Registrar Ingreso
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Movements list */}
                {cashMovements.length > 0 && (
                  <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
                    <h3 className="text-white font-semibold mb-4">Movimientos del Día</h3>
                    <div className="space-y-2">
                      {cashMovements.map((mov) => (
                        <div key={mov.id} className="flex justify-between items-center bg-black/30 rounded-lg p-3">
                          <div className="flex items-center gap-3">
                            {mov.type === 'withdrawal' ? (
                              <ArrowUpCircle className="h-5 w-5 text-red-500" />
                            ) : (
                              <ArrowDownCircle className="h-5 w-5 text-green-500" />
                            )}
                            <div>
                              <p className="text-white">{mov.reason}</p>
                              <p className="text-gray-500 text-sm">
                                {new Date(mov.created_at).toLocaleTimeString('es-MX')}
                              </p>
                            </div>
                          </div>
                          <p className={`font-bold ${mov.type === 'withdrawal' ? 'text-red-500' : 'text-green-500'}`}>
                            {mov.type === 'withdrawal' ? '-' : '+'}${mov.amount.toFixed(2)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Close register button */}
                <div className="bg-gradient-to-r from-orange-900/30 to-red-900/30 border border-orange-500/30 rounded-xl p-6">
                  <h3 className="text-white font-semibold mb-2">Cerrar Caja y Generar Corte</h3>
                  <p className="text-gray-400 mb-4">Al cerrar la caja se generará el reporte completo del día con desglose de productos.</p>
                  <Button onClick={closeCashRegister} className="bg-orange-600 hover:bg-orange-700">
                    <Receipt className="h-4 w-4 mr-2" /> Hacer Corte de Caja
                  </Button>
                </div>
              </>
            )}

            {/* Cash cuts history */}
            {cashHistory.length > 0 && (
              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
                <h3 className="text-white font-semibold mb-4 flex items-center">
                  <BarChart className="h-5 w-5 mr-2 text-purple-500" />
                  Historial de Cortes
                </h3>
                <div className="space-y-4">
                  {cashHistory.slice(0, 7).map((cut) => {
                    const difference = cut.cash_difference;
                    const hasDifference = difference !== null && difference !== undefined;
                    return (
                    <div key={cut.id} className="bg-black/30 rounded-xl p-4" data-testid={`cash-cut-card-${cut.id}`}>
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <p className="text-white font-semibold">{cut.date}</p>
                          <p className="text-gray-500 text-sm">{cut.total_orders} pedidos pagados{cut.refunded_orders_count ? ` · ${cut.refunded_orders_count} reembolsos` : ''}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-gray-400 text-xs">Ventas netas</p>
                          <p className="text-green-500 font-bold text-xl">${(cut.net_sales ?? cut.total_sales ?? 0).toFixed(2)}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-3">
                        <div>
                          <p className="text-gray-500">Fondo inicial</p>
                          <p className="text-white">${(cut.opening_balance ?? 0).toFixed(2)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Cobros efectivo</p>
                          <p className="text-green-400">${(cut.cash_sales ?? 0).toFixed(2)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Transferencia</p>
                          <p className="text-cyan-400">${(cut.transfer_sales ?? 0).toFixed(2)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Tarjeta</p>
                          <p className="text-purple-400">${(cut.card_sales ?? 0).toFixed(2)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Reembolsos</p>
                          <p className="text-red-400">-${(cut.refund_total ?? 0).toFixed(2)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Retiros</p>
                          <p className="text-red-400">-${(cut.total_withdrawals ?? 0).toFixed(2)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Depósitos</p>
                          <p className="text-green-400">+${(cut.total_deposits ?? 0).toFixed(2)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Ventas brutas</p>
                          <p className="text-white">${(cut.gross_sales ?? 0).toFixed(2)}</p>
                        </div>
                      </div>

                      <div className="bg-gray-900/60 border border-gray-800 rounded-lg p-3 grid grid-cols-3 gap-2 text-sm mb-3">
                        <div>
                          <p className="text-gray-500 text-xs">Efectivo teórico</p>
                          <p className="text-orange-400 font-semibold">${(cut.expected_cash ?? 0).toFixed(2)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500 text-xs">Efectivo físico</p>
                          <p className="text-orange-400 font-semibold">
                            {cut.counted_cash !== null && cut.counted_cash !== undefined
                              ? `$${cut.counted_cash.toFixed(2)}`
                              : '—'}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500 text-xs">
                            {!hasDifference ? 'Diferencia' : difference === 0 ? 'Cuadrada' : difference > 0 ? 'Sobrante' : 'Faltante'}
                          </p>
                          <p className={`font-bold ${
                            !hasDifference
                              ? 'text-gray-400'
                              : difference === 0
                                ? 'text-green-400'
                                : difference > 0 ? 'text-blue-400' : 'text-red-400'
                          }`}>
                            {hasDifference ? `$${Math.abs(difference).toFixed(2)}` : '—'}
                          </p>
                        </div>
                      </div>

                      {/* Product breakdown */}
                      {cut.product_breakdown && cut.product_breakdown.length > 0 && (
                        <details className="mt-3">
                          <summary className="text-gray-400 cursor-pointer hover:text-white">
                            Ver desglose de productos
                          </summary>
                          <div className="mt-2 bg-black/30 rounded-lg p-3">
                            {cut.product_breakdown.map((prod, idx) => (
                              <div key={`${cut.id}-${prod.name}-${idx}`} className="flex justify-between text-sm py-1 border-b border-gray-800 last:border-0">
                                <span className="text-gray-300">{prod.quantity}x {prod.name}</span>
                                <span className="text-gray-400">${prod.total?.toFixed(2)}</span>
                              </div>
                            ))}
                          </div>
                        </details>
                      )}
                    </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

      </main>

      {/* Antoni Corte Confirmation Modal */}
      {showAntoniCutModal && antoniSummary && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" data-testid="antoni-cut-modal">
          <div className="bg-gray-900 border border-emerald-500/40 rounded-2xl p-6 max-w-md w-full shadow-2xl">
            <div className="flex items-center mb-4">
              <Receipt className="h-6 w-6 text-emerald-500 mr-2" />
              <h3 className="text-white text-xl font-bold">Corte de Antoni</h3>
            </div>
            <p className="text-gray-400 text-sm mb-4">
              Se marcarán como "pagado" todos los pedidos pendientes que Antoni cobró.
            </p>
            <div className="bg-black/40 border border-gray-800 rounded-xl p-4 space-y-2 mb-4">
              <div className="flex justify-between">
                <span className="text-gray-400">Pedidos a liquidar</span>
                <span className="text-white font-bold">{antoniSummary.pending_count}</span>
              </div>
              <div className="flex justify-between border-t border-gray-700 pt-2">
                <span className="text-gray-300 font-semibold">Antoni debe entregar</span>
                <span className="text-emerald-400 font-bold text-2xl">${antoniSummary.pending_total.toFixed(2)}</span>
              </div>
              {antoniSummary.pending_delivery_fees > 0 && (
                <p className="text-yellow-400/70 text-xs">
                  + ${antoniSummary.pending_delivery_fees.toFixed(2)} de envíos (de Antoni — no entrega esto)
                </p>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setShowAntoniCutModal(false)}
                className="bg-transparent border-gray-700 text-gray-300 hover:bg-gray-800"
                data-testid="antoni-cut-cancel"
              >
                Cancelar
              </Button>
              <Button
                onClick={settleAllAntoniPending}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="antoni-cut-confirm"
              >
                <CheckCircle className="h-4 w-4 mr-2" /> Confirmar Corte
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Antoni Corte Result Modal */}
      {antoniCutResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" data-testid="antoni-cut-result">
          <div className="bg-gray-900 border border-emerald-500/40 rounded-2xl p-6 max-w-md w-full shadow-2xl">
            <div className="flex items-center mb-4">
              <CheckCircle className="h-6 w-6 text-emerald-500 mr-2" />
              <h3 className="text-white text-xl font-bold">Corte completado</h3>
            </div>
            <p className="text-gray-400 text-sm mb-4">{antoniCutResult.date}</p>
            <div className="bg-gradient-to-br from-emerald-900/40 to-green-950/40 border border-emerald-500/40 rounded-xl p-5 space-y-3 mb-4">
              <div className="flex justify-between">
                <span className="text-gray-300">Pedidos liquidados</span>
                <span className="text-white font-bold text-xl">{antoniCutResult.settled_count}</span>
              </div>
              <div className="flex justify-between border-t border-emerald-500/30 pt-3">
                <span className="text-emerald-300 font-semibold">Total recibido de Antoni</span>
                <span className="text-emerald-400 font-bold text-2xl">${antoniCutResult.total.toFixed(2)}</span>
              </div>
              {antoniCutResult.delivery_fees > 0 && (
                <p className="text-yellow-400/70 text-xs">
                  Antoni se queda ${antoniCutResult.delivery_fees.toFixed(2)} de envíos
                </p>
              )}
            </div>
            <div className="flex justify-end">
              <Button
                onClick={() => setAntoniCutResult(null)}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="antoni-cut-result-close"
              >
                Cerrar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Cobrar (Cash Change Calculator) Modal */}
      {showChargeModal && orderToCharge && (() => {
        const total = Number(orderToCharge.total) || 0;
        const selectedMethod = orderToCharge._selectedMethod || orderToCharge.payment_method || 'efectivo';
        const isCash = selectedMethod === 'efectivo';
        const paid = parseFloat(customerPaidWith);
        const validPaid = !Number.isNaN(paid) && paid >= 0;
        const change = validPaid ? paid - total : 0;
        const denominations = [50, 100, 200, 500, 1000];
        // Suggest sensible "exact + small change" amounts above total
        const smartSuggest = (() => {
          if (total <= 0) return [];
          const next50 = Math.ceil(total / 50) * 50;
          const next100 = Math.ceil(total / 100) * 100;
          const next500 = Math.ceil(total / 500) * 500;
          return [...new Set([next50, next100, next500])].filter(v => v !== total).slice(0, 3);
        })();

        const setMethod = (m) => {
          setOrderToCharge(prev => ({ ...prev, _selectedMethod: m }));
          if (m !== 'efectivo') setCustomerPaidWith('');
        };

        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" data-testid="charge-modal">
            <div className="bg-gray-900 border border-green-500/40 rounded-2xl p-6 max-w-lg w-full shadow-2xl">
              <div className="flex items-center mb-4">
                <CircleDollarSign className="h-6 w-6 text-green-500 mr-2" />
                <h3 className="text-white text-xl font-bold">Cobrar Pedido</h3>
              </div>

              <div className="bg-black/40 border border-gray-800 rounded-xl p-3 mb-4">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-400">Pedido</span>
                  <span className="text-orange-400 font-mono">#{orderToCharge.id.slice(0, 8)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-300 text-sm">Total a cobrar</span>
                  <span className="text-green-400 font-bold text-2xl" data-testid="charge-total">
                    ${total.toFixed(2)}
                  </span>
                </div>
              </div>

              <ChargeInvoiceSection
                key={orderToCharge.id}
                order={orderToCharge}
                backendUrl={BACKEND_URL}
                onUpdated={(updated) => {
                  setOrderToCharge(prev => ({ ...updated, _selectedMethod: prev?._selectedMethod }));
                  setCustomerPaidWith('');
                  if (isSplitPayment) {
                    setSplitCash(Number(updated.total).toFixed(2));
                    setSplitCard(''); setSplitTransfer('');
                  }
                  fetchData();
                }}
              />

              {/* Payment method selector */}
              <div className="mb-3">
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-gray-300 text-sm">Método de pago</Label>
                  <button
                    type="button"
                    onClick={() => {
                      setIsSplitPayment(prev => {
                        const next = !prev;
                        if (next) {
                          // Pre-fill cash with full total so cashier only adjusts the card side
                          setSplitCash(total.toFixed(2));
                          setSplitCard('');
                          setSplitTransfer('');
                          setCustomerPaidWith('');
                        } else {
                          setSplitCash(''); setSplitCard(''); setSplitTransfer('');
                        }
                        return next;
                      });
                    }}
                    className={`text-xs px-3 py-1.5 rounded-md font-semibold border transition-colors ${
                      isSplitPayment
                        ? 'bg-amber-600 border-amber-500 text-white'
                        : 'bg-black/40 border-gray-700 text-gray-300 hover:bg-black/60'
                    }`}
                    data-testid="toggle-split-payment"
                  >
                    {isSplitPayment ? '✓ Pago dividido' : '➗ Pago dividido'}
                  </button>
                </div>
                {!isSplitPayment && (
                <div className="grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => setMethod('efectivo')}
                    className={`px-2 py-2.5 rounded-lg border-2 transition-all flex items-center justify-center gap-1.5 text-sm font-semibold ${
                      selectedMethod === 'efectivo'
                        ? 'border-green-500 bg-green-500/20 text-green-400'
                        : 'border-gray-700 bg-black/30 text-gray-400'
                    }`}
                    data-testid="charge-method-efectivo"
                  >
                    <Banknote className="h-4 w-4" /> Efectivo
                  </button>
                  <button
                    type="button"
                    onClick={() => setMethod('transferencia')}
                    className={`px-2 py-2.5 rounded-lg border-2 transition-all flex items-center justify-center gap-1.5 text-sm font-semibold ${
                      selectedMethod === 'transferencia'
                        ? 'border-cyan-500 bg-cyan-500/20 text-cyan-400'
                        : 'border-gray-700 bg-black/30 text-gray-400'
                    }`}
                    data-testid="charge-method-transferencia"
                  >
                    <Smartphone className="h-4 w-4" /> Transfer.
                  </button>
                  <button
                    type="button"
                    onClick={() => setMethod('tarjeta')}
                    className={`px-2 py-2.5 rounded-lg border-2 transition-all flex items-center justify-center gap-1.5 text-sm font-semibold ${
                      selectedMethod === 'tarjeta'
                        ? 'border-purple-500 bg-purple-500/20 text-purple-400'
                        : 'border-gray-700 bg-black/30 text-gray-400'
                    }`}
                    data-testid="charge-method-tarjeta"
                  >
                    <CreditCard className="h-4 w-4" /> Tarjeta
                  </button>
                </div>
                )}
                {!isSplitPayment && selectedMethod !== (orderToCharge.payment_method || 'efectivo') && (
                  <p className="text-yellow-400 text-xs mt-1.5 flex items-center gap-1">
                    ⚠ Cambiarás el método (era: {orderToCharge.payment_method || 'efectivo'})
                  </p>
                )}
              </div>

              {/* Split payment inputs */}
              {isSplitPayment && (() => {
                const sc = Number(splitCash) || 0;
                const scard = Number(splitCard) || 0;
                const st = Number(splitTransfer) || 0;
                const sum = sc + scard + st;
                const diff = Number((total - sum).toFixed(2));
                const matches = Math.abs(diff) < 0.01;
                return (
                  <div className="mb-3 bg-amber-900/15 border border-amber-500/30 rounded-xl p-3">
                    <p className="text-amber-300 text-xs font-semibold mb-2">
                      Divide el total entre 2 o más métodos. La suma debe ser ${total.toFixed(2)}.
                    </p>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <Label className="text-green-400 text-xs flex items-center gap-1 mb-1"><Banknote className="h-3 w-3"/>Efectivo</Label>
                        <Input
                          type="number" step="0.01" min="0" value={splitCash}
                          onChange={(e) => setSplitCash(e.target.value)}
                          placeholder="0.00"
                          className="bg-black/50 border-gray-700 text-white h-10 text-sm"
                          data-testid="split-cash-input"
                        />
                      </div>
                      <div>
                        <Label className="text-purple-400 text-xs flex items-center gap-1 mb-1"><CreditCard className="h-3 w-3"/>Tarjeta</Label>
                        <Input
                          type="number" step="0.01" min="0" value={splitCard}
                          onChange={(e) => setSplitCard(e.target.value)}
                          placeholder="0.00"
                          className="bg-black/50 border-gray-700 text-white h-10 text-sm"
                          data-testid="split-card-input"
                        />
                      </div>
                      <div>
                        <Label className="text-cyan-400 text-xs flex items-center gap-1 mb-1"><Smartphone className="h-3 w-3"/>Transfer</Label>
                        <Input
                          type="number" step="0.01" min="0" value={splitTransfer}
                          onChange={(e) => setSplitTransfer(e.target.value)}
                          placeholder="0.00"
                          className="bg-black/50 border-gray-700 text-white h-10 text-sm"
                          data-testid="split-transfer-input"
                        />
                      </div>
                    </div>
                    <div className={`mt-3 flex justify-between items-center rounded-lg px-3 py-2 ${
                      matches ? 'bg-green-900/40 border border-green-500/40' : 'bg-red-900/30 border border-red-500/40'
                    }`}>
                      <span className="text-xs text-gray-300">Suma</span>
                      <span className={`font-bold ${matches ? 'text-green-400' : 'text-red-400'}`} data-testid="split-sum">
                        ${sum.toFixed(2)}
                        {!matches && (
                          <span className="ml-2 text-xs">
                            ({diff > 0 ? `Falta $${diff.toFixed(2)}` : `Excede $${Math.abs(diff).toFixed(2)}`})
                          </span>
                        )}
                      </span>
                    </div>
                  </div>
                );
              })()}

              {!isSplitPayment && isCash && (
                <div className="mb-3">
                  <Label className="text-gray-300">Paga con $</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    autoFocus
                    value={customerPaidWith}
                    onChange={(e) => setCustomerPaidWith(e.target.value)}
                    placeholder="0.00"
                    className="mt-1 bg-black/50 border-gray-700 text-white text-lg h-12"
                    data-testid="customer-paid-input"
                  />
                </div>
              )}

              {/* Quick denominations - only for cash */}
              {!isSplitPayment && isCash && (
              <div className="mb-3">
                <p className="text-gray-500 text-xs uppercase tracking-wide mb-2">Selección rápida</p>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setCustomerPaidWith(total.toFixed(2))}
                    className="px-3 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white font-bold text-sm border-2 border-green-500"
                    data-testid="paid-exact"
                  >
                    Exacto (${total.toFixed(2)})
                  </button>
                  {smartSuggest.map(v => (
                    <button
                      key={`smart-${v}`}
                      type="button"
                      onClick={() => setCustomerPaidWith(String(v))}
                      className="px-3 py-2 rounded-lg bg-blue-600/30 hover:bg-blue-600/50 text-blue-300 font-bold text-sm border border-blue-500/40"
                      data-testid={`paid-smart-${v}`}
                    >
                      ${v}
                    </button>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {denominations.filter(d => !smartSuggest.includes(d)).map(d => (
                    <button
                      key={`den-${d}`}
                      type="button"
                      onClick={() => setCustomerPaidWith(String(d))}
                      className="px-3 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-200 font-bold text-sm border border-gray-700"
                      data-testid={`paid-${d}`}
                    >
                      ${d}
                    </button>
                  ))}
                </div>
              </div>
              )}

              {/* Change preview */}
              {!isSplitPayment && isCash && validPaid && (
                <div className={`rounded-xl p-4 mb-4 border ${
                  change < 0
                    ? 'bg-red-900/30 border-red-500/40'
                    : change === 0
                      ? 'bg-green-900/30 border-green-500/40'
                      : 'bg-blue-900/30 border-blue-500/40'
                }`}>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300 font-medium">
                      {change < 0 ? 'Falta' : change === 0 ? 'Pago exacto' : 'Cambio'}
                    </span>
                    <span className={`text-2xl font-bold ${
                      change < 0 ? 'text-red-400' : change === 0 ? 'text-green-400' : 'text-blue-400'
                    }`} data-testid="change-amount">
                      ${Math.abs(change).toFixed(2)}
                    </span>
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowChargeModal(false);
                    setOrderToCharge(null);
                    setCustomerPaidWith('');
                    setIsSplitPayment(false);
                    setSplitCash(''); setSplitCard(''); setSplitTransfer('');
                  }}
                  className="bg-transparent border-gray-700 text-gray-300 hover:bg-gray-800"
                  data-testid="charge-cancel-btn"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleConfirmCharge}
                  disabled={
                    isSplitPayment
                      ? Math.abs(total - ((Number(splitCash)||0)+(Number(splitCard)||0)+(Number(splitTransfer)||0))) > 0.01
                      : (isCash && validPaid && change < 0)
                  }
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="charge-confirm-btn"
                >
                  <Printer className="h-4 w-4 mr-2" /> Cobrar e Imprimir
                </Button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Driver cash-out prompt (home delivery paid without enough cash) */}
      {driverCashOutPrompt && (() => {
        const { order, amount } = driverCashOutPrompt;
        return (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4" data-testid="driver-cashout-modal">
            <div className="bg-gray-900 border border-amber-500/50 rounded-2xl p-6 max-w-md w-full shadow-2xl">
              <div className="flex items-center mb-3">
                <Wallet className="h-6 w-6 text-amber-400 mr-2" />
                <h3 className="text-white text-xl font-bold">Pago al Repartidor</h3>
              </div>
              <p className="text-gray-300 text-sm mb-3">
                El pedido <span className="font-mono text-orange-400">#{order.id.slice(0,8)}</span> es a domicilio y se pagó{' '}
                <span className="font-semibold">{order.payment_split ? 'de forma dividida' : (order.payment_method || '').toUpperCase()}</span>.
                Debes entregarle <b className="text-amber-300">${amount.toFixed(2)}</b> en efectivo al repartidor.
              </p>
              <div className="bg-amber-900/20 border border-amber-500/30 rounded-xl p-3 mb-4">
                <div className="flex justify-between text-sm">
                  <span className="text-amber-300">Retiro a registrar</span>
                  <span className="text-amber-100 font-bold text-2xl" data-testid="driver-cashout-amount">${amount.toFixed(2)}</span>
                </div>
                <p className="text-amber-200/70 text-xs mt-1">
                  Motivo: Pago a repartidor · Pedido #{order.id.slice(0,8)}
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setDriverCashOutPrompt(null)}
                  className="flex-1 bg-transparent border-gray-700 text-gray-300 hover:bg-gray-800"
                  data-testid="driver-cashout-later"
                >
                  Más tarde
                </Button>
                <Button
                  onClick={confirmDriverCashOut}
                  className="flex-1 bg-amber-600 hover:bg-amber-500 text-white font-bold"
                  data-testid="driver-cashout-confirm"
                >
                  <Wallet className="h-4 w-4 mr-1.5" /> Confirmar y Abrir Cajón
                </Button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Close Cash Register Modal — Physical count input */}
      {showCloseRegisterModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          data-testid="close-register-modal"
        >
          <div className="bg-gray-900 border border-orange-500/40 rounded-2xl p-6 max-w-lg w-full shadow-2xl">
            <div className="flex items-center mb-4">
              <Receipt className="h-6 w-6 text-orange-500 mr-2" />
              <h3 className="text-white text-xl font-bold">Cerrar Caja — Conteo Físico</h3>
            </div>
            <p className="text-gray-400 text-sm mb-4">
              Cuenta el efectivo físico en el cajón y registra el monto exacto. El sistema
              calculará el faltante o sobrante.
            </p>

            <div className="bg-black/40 border border-gray-800 rounded-xl p-4 mb-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">Fondo inicial</span>
                <span className="text-white">${(cashRegister?.opening_balance || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">+ Cobros efectivo</span>
                <span className="text-green-400">${todayCashSales.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">- Reembolsos efectivo</span>
                <span className="text-red-400">-${todayRefundCash.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">- Salidas / Retiros</span>
                <span className="text-red-400">-${totalWithdrawals.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">+ Depósitos / Ingresos</span>
                <span className="text-green-400">+${totalDeposits.toFixed(2)}</span>
              </div>
              <div className="border-t border-gray-700 pt-2 flex justify-between font-bold">
                <span className="text-white">Efectivo teórico</span>
                <span className="text-orange-400" data-testid="theoretical-cash-value">
                  ${theoreticalCash.toFixed(2)}
                </span>
              </div>
            </div>

            <div className="mb-4">
              <Label className="text-gray-300">Efectivo físico contado ($)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                autoFocus
                value={physicalCountInput}
                onChange={(e) => setPhysicalCountInput(e.target.value)}
                placeholder="0.00"
                className="mt-1 bg-black/50 border-gray-700 text-white text-lg"
                data-testid="physical-count-input"
              />
              {physicalCountInput !== '' && !Number.isNaN(parseFloat(physicalCountInput)) && (
                <p className={`text-sm mt-2 font-semibold ${
                  parseFloat(physicalCountInput) - theoreticalCash === 0
                    ? 'text-green-400'
                    : parseFloat(physicalCountInput) - theoreticalCash > 0
                      ? 'text-blue-400'
                      : 'text-red-400'
                }`} data-testid="cash-difference-preview">
                  Diferencia: ${(parseFloat(physicalCountInput) - theoreticalCash).toFixed(2)}{' '}
                  {parseFloat(physicalCountInput) - theoreticalCash === 0
                    ? '(cuadrada)'
                    : parseFloat(physicalCountInput) - theoreticalCash > 0
                      ? '(sobrante)'
                      : '(faltante)'}
                </p>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setShowCloseRegisterModal(false)}
                disabled={isClosingRegister}
                className="bg-transparent border-gray-700 text-gray-300 hover:bg-gray-800"
                data-testid="close-register-cancel-btn"
              >
                Cancelar
              </Button>
              <Button
                onClick={confirmCloseCashRegister}
                disabled={isClosingRegister || physicalCountInput === ''}
                className="bg-orange-600 hover:bg-orange-700"
                data-testid="close-register-confirm-btn"
              >
                {isClosingRegister ? 'Cerrando...' : 'Confirmar y Cerrar Caja'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Cash Cut Result Modal */}
      {cashCutReport && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 overflow-y-auto"
          data-testid="cash-cut-result-modal"
        >
          <div className="bg-gray-900 border border-green-500/40 rounded-2xl p-6 max-w-2xl w-full shadow-2xl my-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <BarChart className="h-6 w-6 text-green-500 mr-2" />
                <h3 className="text-white text-xl font-bold">Corte de Caja Generado</h3>
              </div>
              <span className="text-gray-400 text-sm">{cashCutReport.date}</span>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm mb-4">
              <div className="bg-black/40 border border-gray-800 rounded-lg p-3">
                <p className="text-gray-500">Fondo inicial</p>
                <p className="text-white font-bold text-lg">${cashCutReport.opening_balance?.toFixed(2)}</p>
              </div>
              <div className="bg-black/40 border border-gray-800 rounded-lg p-3">
                <p className="text-gray-500">Pedidos pagados</p>
                <p className="text-white font-bold text-lg">{cashCutReport.total_orders}</p>
              </div>
              <div className="bg-black/40 border border-gray-800 rounded-lg p-3">
                <p className="text-gray-500">Cobros efectivo</p>
                <p className="text-green-400 font-bold">${cashCutReport.cash_sales?.toFixed(2)}</p>
              </div>
              <div className="bg-black/40 border border-gray-800 rounded-lg p-3">
                <p className="text-gray-500">Cobros transferencia</p>
                <p className="text-cyan-400 font-bold">${cashCutReport.transfer_sales?.toFixed(2)}</p>
              </div>
              <div className="bg-black/40 border border-gray-800 rounded-lg p-3">
                <p className="text-gray-500">Cobros tarjeta</p>
                <p className="text-purple-400 font-bold">${(cashCutReport.card_sales || 0).toFixed(2)}</p>
              </div>
              <div className="bg-black/40 border border-gray-800 rounded-lg p-3">
                <p className="text-gray-500">Reembolsos</p>
                <p className="text-red-400 font-bold">-${(cashCutReport.refund_total || 0).toFixed(2)}</p>
              </div>
              <div className="bg-black/40 border border-gray-800 rounded-lg p-3">
                <p className="text-gray-500">Salidas / Retiros</p>
                <p className="text-red-400 font-bold">-${cashCutReport.total_withdrawals?.toFixed(2)}</p>
              </div>
              <div className="bg-black/40 border border-gray-800 rounded-lg p-3">
                <p className="text-gray-500">Depósitos / Ingresos</p>
                <p className="text-green-400 font-bold">+${(cashCutReport.total_deposits || 0).toFixed(2)}</p>
              </div>
            </div>

            <div className="bg-gradient-to-br from-orange-900/30 to-red-900/20 border border-orange-500/40 rounded-xl p-4 mb-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-300">Ventas brutas</span>
                <span className="text-white font-semibold">${cashCutReport.gross_sales?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-300">Ventas netas (después de reembolsos)</span>
                <span className="text-white font-semibold">${cashCutReport.net_sales?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-300">Efectivo teórico</span>
                <span className="text-orange-400 font-semibold">${cashCutReport.expected_cash?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-300">Efectivo físico contado</span>
                <span className="text-orange-400 font-semibold">
                  ${(cashCutReport.counted_cash ?? 0).toFixed(2)}
                </span>
              </div>
              <div className={`flex justify-between text-base border-t border-orange-500/30 pt-2 font-bold ${
                (cashCutReport.cash_difference ?? 0) === 0
                  ? 'text-green-400'
                  : (cashCutReport.cash_difference ?? 0) > 0 ? 'text-blue-400' : 'text-red-400'
              }`}>
                <span>
                  {(cashCutReport.cash_difference ?? 0) === 0
                    ? 'Caja cuadrada'
                    : (cashCutReport.cash_difference ?? 0) > 0 ? 'Sobrante' : 'Faltante'}
                </span>
                <span data-testid="cut-difference-value">
                  ${Math.abs(cashCutReport.cash_difference ?? 0).toFixed(2)}
                </span>
              </div>
            </div>

            {cashCutReport.delivery_fees > 0 && (
              <div className="text-xs text-yellow-400/80 mb-4">
                Envíos cobrados (no contabilizan): ${cashCutReport.delivery_fees.toFixed(2)}
              </div>
            )}

            <div className="flex justify-end">
              <Button
                onClick={() => setCashCutReport(null)}
                className="bg-green-600 hover:bg-green-700"
                data-testid="close-cut-result-btn"
              >
                Cerrar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
